/**
 * DATA CLEANUP WIZARD
 * VERSION: 2026-01-20-v7 (Auto-dismiss on ALL Apply buttons!)
 * 
 * Comprehensive tool for fixing common data quality issues.
 * 
 * Features:
 * - Barcode extraction from names (e.g., "74 - Classic Horror")
 * - Whitespace trimming
 * - Release info validation
 * - Collection/Event parsing (uses shared collectionParser)
 * - Destash validation
 * 
 * NOW USES SHARED PARSER: collectionParser.ts
 * - One source of truth for parsing logic
 * - Same logic used during import AND cleanup
 * - Supports eventTheme for PPU, HHC, and other theme-based events
 * 
 * REQUIRES: isAdmin: true on your user document in Firestore
 * 
 * SETUP: Add route to App.tsx:
 * import DataCleanupWizard from './pages/DataCleanupWizard';
 * <Route path="/admin/cleanup" element={<DataCleanupWizard />} />
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  collectionGroup,
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  writeBatch,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import {
  Shield,
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  Check,
  X,
  AlertTriangle,
  Sparkles,
  Hash,
  Type,
  Calendar,
  Store,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Database,
  Pencil,
  Save,
  ClipboardList,
  Search,
} from 'lucide-react';

// ============================================
// IMPORT SHARED PARSER - ONE SOURCE OF TRUTH!
// ============================================
import { 
  parseCollectionField,
  type ParsedCollection 
} from '../utils/collectionParser';

// ============================================
// TYPES
// ============================================

interface CleanupItem {
  id: string;
  docRef: any;
  userId: string;
  brand: string;
  name: string;
  category: string;
  // Original values
  original: {
    name: string;
    barcode?: string;
    releaseType?: string;
    eventSource?: string;
    eventName?: string;
    eventTheme?: string;
    collectionName?: string;
    releaseYear?: number;
    releaseSeason?: string;
    box?: string;           // Subscription box (legacy)
    boxName?: string;       // Subscription box name
    boxTheme?: string;      // Subscription box theme
    collab?: string;        // Collaboration (legacy)
    collabWith?: string;    // Who the collab is with
    collabTheme?: string;   // Collab theme
    groupName?: string;     // Group custom group name
    groupTheme?: string;    // Group custom theme
    exclusiveTo?: string;   // Exclusive retailer
    exclusiveTheme?: string; // Exclusive theme
    charityName?: string;   // Charity name
    charityCause?: string;  // Charity cause
    conventionName?: string; // Convention name
    category?: string;      // Polish type (Nail Lacquer, Gel Polish, etc.)
    // Re-release tracking
    isRerelease?: boolean;
    originalReleaseYear?: number;
    originalReleaseSeason?: string;
    releaseNotes?: string;
  };
  // Suggested fixes
  suggested: {
    name?: string;
    barcode?: string;
    releaseType?: string;
    eventSource?: string;
    eventName?: string;
    eventTheme?: string;
    collectionName?: string;
    releaseYear?: number;
    releaseSeason?: string;
    box?: string;           // Subscription box (legacy)
    boxName?: string;       // Subscription box name
    boxTheme?: string;      // Subscription box theme
    collab?: string;        // Collaboration (legacy)
    collabWith?: string;    // Who the collab is with
    collabTheme?: string;   // Collab theme
    groupName?: string;     // Group custom group name
    groupTheme?: string;    // Group custom theme
    exclusiveTo?: string;   // Exclusive retailer
    exclusiveTheme?: string; // Exclusive theme
    charityName?: string;   // Charity name
    charityCause?: string;  // Charity cause
    conventionName?: string; // Convention name
    category?: string;      // Polish type (Nail Lacquer, Gel Polish, etc.)
    parseType?: 'event' | 'collection' | 'both' | 'tag' | 'unknown';
    tags?: string[];  // Tags support for category/subcategory
    // Re-release tracking
    isRerelease?: boolean;
    originalReleaseYear?: number;
    originalReleaseSeason?: string;
    releaseNotes?: string;
  };
  // Issue type
  issueType: 'barcode_in_name' | 'whitespace' | 'release_mismatch' | 'parse_collection' | 'destash_invalid';
  // Selection state
  selected: boolean;
}

type CleanupStep = 'barcode' | 'whitespace' | 'release' | 'parseCollection' | 'destash' | 'explore' | 'search' | 'audit' | 'summary';

// ============================================
// LOCAL PATTERNS (not in shared parser)
// ============================================

// Pattern: "123 - Name" or "123- Name" or "123-Name" or "#123 - Name"
const BARCODE_IN_NAME_PATTERN = /^#?(\d+)\s*[-–—]\s*(.+)$/;

// ============================================
// COMPONENT
// ============================================

export default function DataCleanupWizard() {
  // Auth state
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  // Wizard state
  const [currentStep, setCurrentStep] = useState<CleanupStep>('barcode');
  const [isScanning, setIsScanning] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  // Items found for each issue type
  const [barcodeItems, setBarcodeItems] = useState<CleanupItem[]>([]);
  const [dismissedBarcodeItems, setDismissedBarcodeItems] = useState<CleanupItem[]>([]);
  const [dismissedBarcodeIds, setDismissedBarcodeIds] = useState<Set<string>>(new Set());
  const [whitespaceItems, setWhitespaceItems] = useState<CleanupItem[]>([]);
  const [dismissedWhitespaceIds, setDismissedWhitespaceIds] = useState<Set<string>>(new Set());
  const [releaseItems, setReleaseItems] = useState<CleanupItem[]>([]);
  const [dismissedReleaseIds, setDismissedReleaseIds] = useState<Set<string>>(new Set());
  const [parseCollectionItems, setParseCollectionItems] = useState<CleanupItem[]>([]);
  const [dismissedParseCollectionItems, setDismissedParseCollectionItems] = useState<CleanupItem[]>([]);
  const [dismissedParseCollectionIds, setDismissedParseCollectionIds] = useState<Set<string>>(new Set());
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_dismissalsLoaded, setDismissalsLoaded] = useState(false);
  const [destashItems, setDestashItems] = useState<CleanupItem[]>([]);
  const [dismissedDestashIds, setDismissedDestashIds] = useState<Set<string>>(new Set());

  // Data Explorer state
  type ExplorerField = 'collabWith' | 'collabTheme' | 'collab' | 'box' | 'boxName' | 'boxTheme' | 'eventSource' | 'eventName' | 'eventTheme' | 'collectionName' | 'groupName' | 'groupTheme' | 'exclusiveTo' | 'exclusiveTheme' | 'charityName' | 'charityCause' | 'conventionName' | 'category' | 'tags' | 'brand';
  interface FieldValue {
    value: string;
    count: number;
    items: { id: string; docRef: any; name: string; brand: string; userId: string }[];
  }
  const [explorerField, setExplorerField] = useState<ExplorerField>('collabWith');
  const [explorerData, setExplorerData] = useState<FieldValue[]>([]);
  const [explorerLoading, setExplorerLoading] = useState(false);
  const [expandedValues, setExpandedValues] = useState<Set<string>>(new Set());
  const [editingValue, setEditingValue] = useState<string | null>(null);
  const [editNewValue, setEditNewValue] = useState('');

  // Applied counts
  const [appliedCounts, setAppliedCounts] = useState({
    barcode: 0,
    whitespace: 0,
    release: 0,
    parseCollection: 0,
    destash: 0,
  });

  // Expanded sections
  const [expandedBrands, setExpandedBrands] = useState<Set<string>>(new Set());

  // Data Audit state
  interface AuditFieldData {
    value: string;
    count: number;
    itemIds: string[];
  }
  interface AuditData {
    eventSource: AuditFieldData[];
    eventTheme: AuditFieldData[];
    collectionName: AuditFieldData[];
    boxName: AuditFieldData[];
    boxTheme: AuditFieldData[];
    groupName: AuditFieldData[];
    groupTheme: AuditFieldData[];
    exclusiveTo: AuditFieldData[];
    exclusiveTheme: AuditFieldData[];
    charityName: AuditFieldData[];
    charityCause: AuditFieldData[];
    conventionName: AuditFieldData[];
    collabWith: AuditFieldData[];
    collabTheme: AuditFieldData[];
    setName: AuditFieldData[];         // Set/duo name
    setType: AuditFieldData[];         // Set type (duo, trio, quad, set)
    collab: AuditFieldData[]; // Legacy
    box: AuditFieldData[]; // Legacy
    brand: AuditFieldData[];
    releaseType: AuditFieldData[];
    releaseSeason: AuditFieldData[];
    category: AuditFieldData[]; // Item type (Nail Lacquer, Gel Polish, etc.)
    releaseNotes: AuditFieldData[]; // Notes about the release
    originalReleaseSeason: AuditFieldData[]; // For re-releases
  }
  const [auditData, setAuditData] = useState<AuditData | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditExpandedSections, setAuditExpandedSections] = useState<Set<string>>(new Set(['eventSource', 'collectionName', 'boxName']));
  const [auditEditModal, setAuditEditModal] = useState<{ field: keyof AuditData; oldValue: string; itemIds: string[] } | null>(null);
  const [auditEditNewValue, setAuditEditNewValue] = useState('');
  const [auditApplying, setAuditApplying] = useState(false);
  const [auditSortMode, setAuditSortMode] = useState<'count' | 'alpha'>('count');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_auditMigrateModal, _setAuditMigrateModal] = useState<{ fromField: 'box' | 'collab'; toField: 'boxName' | 'collabWith'; value: string; itemIds: string[] } | null>(null);
  const [auditMoveModal, setAuditMoveModal] = useState<{ fromField: keyof AuditData; value: string; itemIds: string[] } | null>(null);
  const [auditMoveTargetField, setAuditMoveTargetField] = useState<string>('');
  const [dateFixResults, setDateFixResults] = useState<{ found: number; fixed: number } | null>(null);

  // Database Search state
  interface SearchResult {
    id: string;
    docPath: string;
    userId: string;
    brand: string;
    name: string;
    category: string;
    matchedFields: { field: string; value: string }[];
    data: Record<string, any>;
  }
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searchEditModal, setSearchEditModal] = useState<SearchResult | null>(null);
  const [searchEditData, setSearchEditData] = useState<Record<string, any>>({});
  const [searchSaving, setSearchSaving] = useState(false);

  // Check admin status
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user?.uid || null);

      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setIsAdmin(userDoc.data()?.isAdmin === true);
        }
      } else {
        setIsAdmin(false);
      }
      setCheckingAdmin(false);
    });
    return () => unsubscribe();
  }, []);

  // Load persisted dismissals from Firestore
  useEffect(() => {
    const loadDismissals = async () => {
      try {
        // Load collection cleanup dismissals
        const collectionDismissalsDoc = await getDoc(doc(db, 'adminDismissals', 'collectionCleanup'));
        if (collectionDismissalsDoc.exists()) {
          const data = collectionDismissalsDoc.data();
          const dismissedIds = new Set<string>(data.dismissedItemIds || []);
          setDismissedParseCollectionIds(dismissedIds);
        } else {
        }
        
        // Load barcode cleanup dismissals
        const barcodeDismissalsDoc = await getDoc(doc(db, 'adminDismissals', 'barcodeCleanup'));
        if (barcodeDismissalsDoc.exists()) {
          const data = barcodeDismissalsDoc.data();
          const dismissedIds = new Set<string>(data.dismissedItemIds || []);
          setDismissedBarcodeIds(dismissedIds);
        } else {
        }
        
        // Load whitespace cleanup dismissals
        const whitespaceDismissalsDoc = await getDoc(doc(db, 'adminDismissals', 'whitespaceCleanup'));
        if (whitespaceDismissalsDoc.exists()) {
          const data = whitespaceDismissalsDoc.data();
          const dismissedIds = new Set<string>(data.dismissedItemIds || []);
          setDismissedWhitespaceIds(dismissedIds);
        } else {
        }
        
        // Load release cleanup dismissals
        const releaseDismissalsDoc = await getDoc(doc(db, 'adminDismissals', 'releaseCleanup'));
        if (releaseDismissalsDoc.exists()) {
          const data = releaseDismissalsDoc.data();
          const dismissedIds = new Set<string>(data.dismissedItemIds || []);
          setDismissedReleaseIds(dismissedIds);
        } else {
        }
        
        // Load destash cleanup dismissals
        const destashDismissalsDoc = await getDoc(doc(db, 'adminDismissals', 'destashCleanup'));
        if (destashDismissalsDoc.exists()) {
          const data = destashDismissalsDoc.data();
          const dismissedIds = new Set<string>(data.dismissedItemIds || []);
          setDismissedDestashIds(dismissedIds);
        } else {
        }
      } catch (error) {
      } finally {
        setDismissalsLoaded(true);
      }
    };
    loadDismissals();
  }, []);

  // Helper to persist a dismissal to Firestore
  const persistDismissal = async (itemId: string, _originalValue: string) => {
    try {
      const dismissalsRef = doc(db, 'adminDismissals', 'collectionCleanup');
      await setDoc(dismissalsRef, {
        dismissedItemIds: arrayUnion(itemId),
        lastUpdated: new Date().toISOString(),
      }, { merge: true });
      setDismissedParseCollectionIds(prev => new Set([...prev, itemId]));
    } catch (error) {
    }
  };

  // Helper to remove a dismissal from Firestore
  const removeDismissal = async (itemId: string) => {
    try {
      const dismissalsRef = doc(db, 'adminDismissals', 'collectionCleanup');
      await setDoc(dismissalsRef, {
        dismissedItemIds: arrayRemove(itemId),
        lastUpdated: new Date().toISOString(),
      }, { merge: true });
      setDismissedParseCollectionIds(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    } catch (error) {
    }
  };

  // Helper to persist a BARCODE dismissal to Firestore
  const persistBarcodeDismissal = async (itemId: string) => {
    try {
      const dismissalsRef = doc(db, 'adminDismissals', 'barcodeCleanup');
      await setDoc(dismissalsRef, {
        dismissedItemIds: arrayUnion(itemId),
        lastUpdated: new Date().toISOString(),
      }, { merge: true });
      setDismissedBarcodeIds(prev => new Set([...prev, itemId]));
    } catch (error) {
    }
  };

  // Helper to persist a WHITESPACE dismissal to Firestore
  const persistWhitespaceDismissal = async (itemId: string) => {
    try {
      const dismissalsRef = doc(db, 'adminDismissals', 'whitespaceCleanup');
      await setDoc(dismissalsRef, {
        dismissedItemIds: arrayUnion(itemId),
        lastUpdated: new Date().toISOString(),
      }, { merge: true });
      setDismissedWhitespaceIds(prev => new Set([...prev, itemId]));
    } catch (error) {
    }
  };

  // Helper to persist a RELEASE dismissal to Firestore
  const persistReleaseDismissal = async (itemId: string) => {
    try {
      const dismissalsRef = doc(db, 'adminDismissals', 'releaseCleanup');
      await setDoc(dismissalsRef, {
        dismissedItemIds: arrayUnion(itemId),
        lastUpdated: new Date().toISOString(),
      }, { merge: true });
      setDismissedReleaseIds(prev => new Set([...prev, itemId]));
    } catch (error) {
    }
  };

  // Helper to persist a DESTASH dismissal to Firestore
  const persistDestashDismissal = async (itemId: string) => {
    try {
      const dismissalsRef = doc(db, 'adminDismissals', 'destashCleanup');
      await setDoc(dismissalsRef, {
        dismissedItemIds: arrayUnion(itemId),
        lastUpdated: new Date().toISOString(),
      }, { merge: true });
      setDismissedDestashIds(prev => new Set([...prev, itemId]));
    } catch (error) {
    }
  };

  // ============================================
  // SCANNING FUNCTIONS
  // ============================================

  const scanForBarcodeInName = async () => {
    setIsScanning(true);
    
    // Reload dismissals fresh before scanning to ensure we have the latest
    let currentDismissedIds = new Set<string>();
    try {
      const dismissalsDoc = await getDoc(doc(db, 'adminDismissals', 'barcodeCleanup'));
      if (dismissalsDoc.exists()) {
        const data = dismissalsDoc.data();
        currentDismissedIds = new Set<string>(data.dismissedItemIds || []);
        setDismissedBarcodeIds(currentDismissedIds);
      }
    } catch (error) {
      currentDismissedIds = dismissedBarcodeIds; // Fall back to current state
    }
    
    const items: CleanupItem[] = [];

    try {
      const snapshot = await getDocs(collectionGroup(db, 'items'));

      snapshot.docs.forEach((docSnap) => {
        // Skip items that have been dismissed
        if (currentDismissedIds.has(docSnap.id)) {
          return;
        }
        
        const data = docSnap.data();
        const name = data.name || '';

        const match = name.match(BARCODE_IN_NAME_PATTERN);
        if (match) {
          const extractedBarcode = match[1];
          const extractedName = match[2].trim();

          if (!data.barcode || data.barcode !== extractedBarcode) {
            items.push({
              id: docSnap.id,
              docRef: docSnap.ref,
              userId: docSnap.ref.parent.parent?.id || 'unknown',
              brand: data.brand || 'Unknown',
              name: data.name,
              category: data.category || 'polish',
              original: {
                name: data.name,
                barcode: data.barcode,
              },
              suggested: {
                name: extractedName,
                barcode: extractedBarcode,
              },
              issueType: 'barcode_in_name',
              selected: true,
            });
          }
        }
      });

      items.sort((a, b) => a.brand.localeCompare(b.brand));
      setBarcodeItems(items);
    } catch (error) {
    }

    setIsScanning(false);
  };

  const scanForWhitespace = async () => {
    setIsScanning(true);
    
    // Reload dismissals fresh before scanning
    let currentDismissedIds = new Set<string>();
    try {
      const dismissalsDoc = await getDoc(doc(db, 'adminDismissals', 'whitespaceCleanup'));
      if (dismissalsDoc.exists()) {
        const data = dismissalsDoc.data();
        currentDismissedIds = new Set<string>(data.dismissedItemIds || []);
        setDismissedWhitespaceIds(currentDismissedIds);
      }
    } catch (error) {
      currentDismissedIds = dismissedWhitespaceIds;
    }
    
    const items: CleanupItem[] = [];

    try {
      const snapshot = await getDocs(collectionGroup(db, 'items'));

      snapshot.docs.forEach((docSnap) => {
        // Skip dismissed items
        if (currentDismissedIds.has(docSnap.id)) {
          return;
        }
        
        const data = docSnap.data();
        const name = data.name || '';
        const brand = data.brand || '';

        const cleanWhitespace = (str: string): string => {
          let cleaned = str.trim();
          cleaned = cleaned.replace(/\s{3,}/g, '  ');
          cleaned = cleaned.replace(/([^.:!?])  +/g, '$1 ');
          cleaned = cleaned.replace(/^  +/, ' ');
          return cleaned;
        };

        const trimmedName = cleanWhitespace(name);
        const trimmedBrand = cleanWhitespace(brand);

        const hasIssue = name !== trimmedName || brand !== trimmedBrand;

        if (hasIssue) {
          items.push({
            id: docSnap.id,
            docRef: docSnap.ref,
            userId: docSnap.ref.parent.parent?.id || 'unknown',
            brand: data.brand || 'Unknown',
            name: data.name,
            category: data.category || 'polish',
            original: {
              name: data.name,
            },
            suggested: {
              name: trimmedName !== name ? trimmedName : undefined,
            },
            issueType: 'whitespace',
            selected: true,
          });
        }
      });

      items.sort((a, b) => a.brand.localeCompare(b.brand));
      setWhitespaceItems(items);
    } catch (error) {
    }

    setIsScanning(false);
  };

  const scanForReleaseMismatch = async () => {
    setIsScanning(true);
    
    // Reload dismissals fresh before scanning
    let currentDismissedIds = new Set<string>();
    try {
      const dismissalsDoc = await getDoc(doc(db, 'adminDismissals', 'releaseCleanup'));
      if (dismissalsDoc.exists()) {
        const data = dismissalsDoc.data();
        currentDismissedIds = new Set<string>(data.dismissedItemIds || []);
        setDismissedReleaseIds(currentDismissedIds);
      }
    } catch (error) {
      currentDismissedIds = dismissedReleaseIds;
    }
    
    const items: CleanupItem[] = [];

    try {
      const snapshot = await getDocs(collectionGroup(db, 'items'));

      snapshot.docs.forEach((docSnap) => {
        // Skip dismissed items
        if (currentDismissedIds.has(docSnap.id)) {
          return;
        }
        
        const data = docSnap.data();
        const releaseType = (data.releaseType || '').toLowerCase();
        const eventSource = data.eventSource || '';
        const collectionName = data.collectionName || '';

        if (releaseType === 'collection' && eventSource && !collectionName) {
          items.push({
            id: docSnap.id,
            docRef: docSnap.ref,
            userId: docSnap.ref.parent.parent?.id || 'unknown',
            brand: data.brand || 'Unknown',
            name: data.name || '',
            category: data.category || 'polish',
            original: {
              name: data.name,
              releaseType: data.releaseType,
              eventSource: data.eventSource,
              collectionName: data.collectionName,
            },
            suggested: {
              releaseType: 'Event / Box',
            },
            issueType: 'release_mismatch',
            selected: true,
          });
        }

        if ((releaseType === 'event' || releaseType === 'event / box') && collectionName && !eventSource) {
          items.push({
            id: docSnap.id,
            docRef: docSnap.ref,
            userId: docSnap.ref.parent.parent?.id || 'unknown',
            brand: data.brand || 'Unknown',
            name: data.name || '',
            category: data.category || 'polish',
            original: {
              name: data.name,
              releaseType: data.releaseType,
              eventSource: data.eventSource,
              collectionName: data.collectionName,
            },
            suggested: {
              releaseType: 'Collection',
            },
            issueType: 'release_mismatch',
            selected: true,
          });
        }

        if ((!releaseType || releaseType === 'unknown') && eventSource) {
          items.push({
            id: docSnap.id,
            docRef: docSnap.ref,
            userId: docSnap.ref.parent.parent?.id || 'unknown',
            brand: data.brand || 'Unknown',
            name: data.name || '',
            category: data.category || 'polish',
            original: {
              name: data.name,
              releaseType: data.releaseType,
              eventSource: data.eventSource,
            },
            suggested: {
              releaseType: 'Event / Box',
            },
            issueType: 'release_mismatch',
            selected: true,
          });
        }

        if ((!releaseType || releaseType === 'unknown') && collectionName && !eventSource) {
          items.push({
            id: docSnap.id,
            docRef: docSnap.ref,
            userId: docSnap.ref.parent.parent?.id || 'unknown',
            brand: data.brand || 'Unknown',
            name: data.name || '',
            category: data.category || 'polish',
            original: {
              name: data.name,
              releaseType: data.releaseType,
              collectionName: data.collectionName,
            },
            suggested: {
              releaseType: 'Collection',
            },
            issueType: 'release_mismatch',
            selected: true,
          });
        }
      });

      items.sort((a, b) => a.brand.localeCompare(b.brand));
      setReleaseItems(items);
    } catch (error) {
    }

    setIsScanning(false);
  };

  // ============================================
  // PARSE COLLECTIONS - Uses shared parser!
  // Now includes eventTheme support
  // ============================================
  const scanForParseableCollections = async () => {
    setIsScanning(true);
    
    // Reload dismissals fresh before scanning to ensure we have the latest
    let currentDismissedIds = new Set<string>();
    try {
      const dismissalsDoc = await getDoc(doc(db, 'adminDismissals', 'collectionCleanup'));
      if (dismissalsDoc.exists()) {
        const data = dismissalsDoc.data();
        currentDismissedIds = new Set<string>(data.dismissedItemIds || []);
        setDismissedParseCollectionIds(currentDismissedIds);
      }
    } catch (error) {
      currentDismissedIds = dismissedParseCollectionIds; // Fall back to current state
    }
    
    
    const items: CleanupItem[] = [];

    try {
      const snapshot = await getDocs(collectionGroup(db, 'items'));

      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const collectionName = data.collectionName || '';
        
        if (!collectionName.trim()) return;
        
        // Skip items that have been dismissed (using fresh data)
        if (currentDismissedIds.has(docSnap.id)) {
          return;
        }
        
        // Use the SHARED parser from collectionParser.ts!
        const parsed: ParsedCollection = parseCollectionField(collectionName);
        
        // Skip if nothing to parse
        if (parsed.parseType === 'unknown' && !parsed.releaseYear && !parsed.releaseSeason) return;
        
        // Skip if the ONLY thing we found was a season, and it would leave collection empty
        if (!parsed.collectionName && !parsed.eventName && !parsed.eventTheme && parsed.releaseSeason && !parsed.releaseYear) {
          return;
        }

        // Skip if parsing would result in empty collection AND no event
        if (!parsed.collectionName && !parsed.eventName && !parsed.eventTheme && parsed.parseType === 'unknown') {
          return;
        }
        
        // Check if there's actually something meaningful to fix
        const needsFix = 
          parsed.parseType === 'tag' ||
          parsed.parseType === 'event' ||
          parsed.parseType === 'both' ||
          (parsed.releaseYear && (parsed.collectionName || parsed.eventTheme)) ||
          (parsed.releaseSeason && (parsed.collectionName || parsed.eventTheme)) ||
          (parsed.collectionName && parsed.collectionName !== collectionName) ||
          (parsed.eventTheme); // Theme-based events always need fixing
        
        if (needsFix) {
          // For tags, split on hyphen to get category/subcategory
          const suggestedTags = parsed.parseType === 'tag' 
            ? collectionName.split('-').map((t: string) => t.trim()).filter((t: string) => t.length > 0)
            : undefined;
          
          items.push({
            id: docSnap.id,
            docRef: docSnap.ref,
            userId: docSnap.ref.parent.parent?.id || 'unknown',
            brand: data.brand || 'Unknown',
            name: data.name || '',
            category: data.category || 'polish',
            original: {
              name: data.name || '',
              collectionName: collectionName,
              releaseType: data.releaseType,
              eventSource: data.eventSource,
              eventTheme: data.eventTheme,
              releaseYear: data.releaseYear,
              releaseSeason: data.releaseSeason,
            },
            suggested: {
              parseType: parsed.parseType,
              collectionName: parsed.collectionName,
              eventSource: parsed.eventName,
              eventTheme: parsed.eventTheme, // NEW: Theme support!
              releaseType: parsed.parseType === 'event' ? 'Event / Box' : 
                          (parsed.parseType === 'collection' || parsed.parseType === 'both') ? 'Collection' : undefined,
              releaseYear: parsed.releaseYear,
              releaseSeason: parsed.releaseSeason,
              tags: suggestedTags,  // NEW: Pre-populated tags
            },
            issueType: 'parse_collection',
            selected: parsed.confidence === 'high' && parsed.parseType !== 'tag',
          });
        }
      });

      items.sort((a, b) => {
        if (a.userId !== b.userId) return a.userId.localeCompare(b.userId);
        return a.brand.localeCompare(b.brand);
      });
      
      setParseCollectionItems(items);
    } catch (error) {
    }

    setIsScanning(false);
  };

  const scanForDestashIssues = async () => {
    setIsScanning(true);
    
    // Reload dismissals fresh before scanning
    let currentDismissedIds = new Set<string>();
    try {
      const dismissalsDoc = await getDoc(doc(db, 'adminDismissals', 'destashCleanup'));
      if (dismissalsDoc.exists()) {
        const data = dismissalsDoc.data();
        currentDismissedIds = new Set<string>(data.dismissedItemIds || []);
        setDismissedDestashIds(currentDismissedIds);
      }
    } catch (error) {
      currentDismissedIds = dismissedDestashIds;
    }
    
    const items: CleanupItem[] = [];

    try {
      const snapshot = await getDocs(collectionGroup(db, 'items'));

      snapshot.docs.forEach((docSnap) => {
        // Skip dismissed items
        if (currentDismissedIds.has(docSnap.id)) {
          return;
        }
        
        const data = docSnap.data();

        if (data.isForDestash) {
          const issues: string[] = [];

          if (!data.destashCondition) issues.push('missing condition');
          if (!data.destashPrice && data.destashPrice !== 0) issues.push('missing price');
          if (data.destashQuantityAvailable > (data.quantity || 1)) issues.push('qty mismatch');

          if (issues.length > 0) {
            items.push({
              id: docSnap.id,
              docRef: docSnap.ref,
              userId: docSnap.ref.parent.parent?.id || 'unknown',
              brand: data.brand || 'Unknown',
              name: data.name || '',
              category: data.category || 'polish',
              original: {
                name: issues.join(', '),
              },
              suggested: {},
              issueType: 'destash_invalid',
              selected: false,
            });
          }
        }
      });

      items.sort((a, b) => a.brand.localeCompare(b.brand));
      setDestashItems(items);
    } catch (error) {
    }

    setIsScanning(false);
  };

  // ============================================
  // DATA EXPLORER FUNCTIONS
  // ============================================
  
  const scanForExplorerField = async (field: ExplorerField) => {
    setExplorerLoading(true);
    setExplorerData([]);
    setExpandedValues(new Set());
    
    const valueMap = new Map<string, FieldValue>();
    
    try {
      const snapshot = await getDocs(collectionGroup(db, 'items'));
      
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        let values: string[] = [];
        
        // Handle different field types
        if (field === 'tags') {
          // Tags is an array
          values = (data.tags || []).filter((t: string) => t && t.trim());
        } else {
          // Other fields are strings
          const val = data[field];
          if (val && typeof val === 'string' && val.trim()) {
            values = [val.trim()];
          }
        }
        
        // Add to map
        values.forEach(value => {
          if (!valueMap.has(value)) {
            valueMap.set(value, { value, count: 0, items: [] });
          }
          const entry = valueMap.get(value)!;
          entry.count++;
          entry.items.push({
            id: docSnap.id,
            docRef: docSnap.ref,
            name: data.name || 'Unknown',
            brand: data.brand || 'Unknown',
            userId: docSnap.ref.parent.parent?.id || 'unknown',
          });
        });
      });
      
      // Convert to array and sort by count descending
      const results = Array.from(valueMap.values()).sort((a, b) => b.count - a.count);
      setExplorerData(results);
      
    } catch (error) {
    }
    
    setExplorerLoading(false);
  };
  
  const applyExplorerEdit = async (oldValue: string, newValue: string, field: ExplorerField) => {
    if (!newValue.trim() || oldValue === newValue) {
      setEditingValue(null);
      return;
    }
    
    const entry = explorerData.find(e => e.value === oldValue);
    if (!entry) return;
    
    setIsApplying(true);
    
    try {
      let batch = writeBatch(db);
      let batchCount = 0;
      
      for (const item of entry.items) {
        if (field === 'tags') {
          // For tags, we need to replace the old tag with the new one in the array
          // We'll need to fetch the current tags first
          const itemDoc = await getDoc(item.docRef);
          if (itemDoc.exists()) {
            const data = itemDoc.data() as { tags?: string[] };
            const currentTags: string[] = data.tags || [];
            const newTags = currentTags.map(t => t === oldValue ? newValue.trim() : t);
            batch.update(item.docRef, { tags: newTags });
          }
        } else {
          batch.update(item.docRef, { [field]: newValue.trim() });
        }
        
        batchCount++;
        
        if (batchCount >= 450) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }
      
      if (batchCount > 0) {
        await batch.commit();
      }
      
      
      // Refresh the data
      await scanForExplorerField(field);
      
    } catch (error) {
    }
    
    setEditingValue(null);
    setEditNewValue('');
    setIsApplying(false);
  };

  // ============================================
  // AUDIT SCAN FUNCTION
  // ============================================
  
  const scanForAuditData = async () => {
    setAuditLoading(true);
    
    try {
      // Initialize field maps
      const fieldMaps: Record<keyof AuditData, Map<string, { count: number; itemIds: string[] }>> = {
        eventSource: new Map(),
        eventTheme: new Map(),
        collectionName: new Map(),
        boxName: new Map(),
        boxTheme: new Map(),
        groupName: new Map(),
        groupTheme: new Map(),
        exclusiveTo: new Map(),
        exclusiveTheme: new Map(),
        charityName: new Map(),
        charityCause: new Map(),
        conventionName: new Map(),
        collabWith: new Map(),
        collabTheme: new Map(),
        setName: new Map(),
        setType: new Map(),
        collab: new Map(),
        box: new Map(),
        brand: new Map(),
        releaseType: new Map(),
        releaseSeason: new Map(),
        category: new Map(),
        releaseNotes: new Map(),
        originalReleaseSeason: new Map(),
      };
      
      // Scan all polish items
      const snapshot = await getDocs(collectionGroup(db, 'items'));
      
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const itemId = docSnap.ref.path; // Full path for unique identification
        
        // Process each field
        for (const [fieldName, fieldMap] of Object.entries(fieldMaps)) {
          const value = data[fieldName];
          if (value && typeof value === 'string' && value.trim()) {
            const trimmedValue = value.trim();
            if (!fieldMap.has(trimmedValue)) {
              fieldMap.set(trimmedValue, { count: 0, itemIds: [] });
            }
            const entry = fieldMap.get(trimmedValue)!;
            entry.count++;
            entry.itemIds.push(itemId);
          }
        }
      });
      
      // Convert maps to sorted arrays
      const result: AuditData = {} as AuditData;
      for (const [fieldName, fieldMap] of Object.entries(fieldMaps)) {
        const entries: AuditFieldData[] = [];
        fieldMap.forEach((data, value) => {
          entries.push({ value, count: data.count, itemIds: data.itemIds });
        });
        // Sort by count descending
        entries.sort((a, b) => b.count - a.count);
        (result as any)[fieldName] = entries;
      }
      
      setAuditData(result);
      
    } catch (error) {
    }
    
    setAuditLoading(false);
  };
  
  const applyAuditEdit = async () => {
    if (!auditEditModal) return;
    
    setAuditApplying(true);
    
    try {
      const { field, itemIds } = auditEditModal;
      const newValue = auditEditNewValue.trim();
      
      let batch = writeBatch(db);
      let batchCount = 0;
      let updated = 0;
      
      for (const itemPath of itemIds) {
        const itemRef = doc(db, itemPath);
        
        // Set to new value, or delete field if empty
        if (newValue) {
          batch.update(itemRef, { [field]: newValue });
        } else {
          batch.update(itemRef, { [field]: '' });
        }
        
        batchCount++;
        updated++;
        
        if (batchCount >= 450) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }
      
      if (batchCount > 0) {
        await batch.commit();
      }
      
      
      // Refresh audit data
      await scanForAuditData();
      
    } catch (error) {
    }
    
    setAuditEditModal(null);
    setAuditEditNewValue('');
    setAuditApplying(false);
  };
  
  // Migration function for legacy fields
  // Also moves eventTheme → boxTheme when migrating box field!
  const applyAuditMigrate = async (legacyField: 'box' | 'collab', value: string, itemIds: string[]) => {
    setAuditApplying(true);
    
    try {
      let batch = writeBatch(db);
      let batchCount = 0;
      let migrated = 0;
      let themesMoved = 0;
      
      // Determine target field based on legacy field
      const targetField = legacyField === 'box' ? 'boxName' : 'collabWith';
      const targetThemeField = legacyField === 'box' ? 'boxTheme' : 'collabTheme';
      const releaseTypeUpdate = legacyField === 'box' ? 'box' : null;
      
      for (const itemPath of itemIds) {
        const itemRef = doc(db, itemPath);
        
        // Read the document to check for eventTheme
        const docSnap = await getDoc(itemRef);
        const data = docSnap.exists() ? docSnap.data() : {};
        
        // Build update: copy to new field, clear legacy field
        const updates: Record<string, any> = {
          [targetField]: value,  // Copy to new field
          [legacyField]: '',     // Clear legacy field
        };
        
        // If migrating box and there's an eventTheme, move it to boxTheme
        // If migrating collab and there's an eventTheme, move it to collabTheme
        if (data.eventTheme) {
          updates[targetThemeField] = data.eventTheme;  // Copy theme to new field
          updates.eventTheme = '';                       // Clear old theme field
          themesMoved++;
        }
        
        // If migrating box, also set releaseType to 'box'
        if (releaseTypeUpdate) {
          updates.releaseType = releaseTypeUpdate;
        }
        
        batch.update(itemRef, updates);
        batchCount++;
        migrated++;
        
        if (batchCount >= 450) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }
      
      if (batchCount > 0) {
        await batch.commit();
      }
      
      
      // Refresh audit data
      await scanForAuditData();
      
    } catch (error) {
    }
    
    setAuditApplying(false);
  };
  
  // Migrate ALL items for a legacy field (also moves themes!)
  const migrateAllLegacyField = async (legacyField: 'box' | 'collab') => {
    if (!auditData) return;
    
    const fieldData = legacyField === 'box' ? auditData.box : auditData.collab;
    if (fieldData.length === 0) return;
    
    const totalItems = fieldData.reduce((sum, d) => sum + d.count, 0);
    const targetField = legacyField === 'box' ? 'boxName' : 'collabWith';
    const targetThemeField = legacyField === 'box' ? 'boxTheme' : 'collabTheme';
    
    if (!window.confirm(`Migrate all ${totalItems} items from "${legacyField}" to "${targetField}"?\n\nThis will:\n• Copy values to ${targetField}\n• Move any eventTheme → ${targetThemeField}\n• Clear the legacy ${legacyField} field\n${legacyField === 'box' ? '• Set releaseType to "box"' : ''}`)) {
      return;
    }
    
    setAuditApplying(true);
    
    try {
      let batch = writeBatch(db);
      let batchCount = 0;
      let migrated = 0;
      let themesMoved = 0;
      
      // Collect all item IDs and their values
      for (const entry of fieldData) {
        for (const itemPath of entry.itemIds) {
          const itemRef = doc(db, itemPath);
          
          // Read the document to check for eventTheme
          const docSnap = await getDoc(itemRef);
          const data = docSnap.exists() ? docSnap.data() : {};
          
          const updates: Record<string, any> = {
            [targetField]: entry.value,  // Copy to new field
            [legacyField]: '',           // Clear legacy field
          };
          
          // Move eventTheme to the appropriate theme field
          if (data.eventTheme) {
            updates[targetThemeField] = data.eventTheme;
            updates.eventTheme = '';
            themesMoved++;
          }
          
          // If migrating box, also set releaseType
          if (legacyField === 'box') {
            updates.releaseType = 'box';
          }
          
          batch.update(itemRef, updates);
          batchCount++;
          migrated++;
          
          if (batchCount >= 450) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          }
        }
      }
      
      if (batchCount > 0) {
        await batch.commit();
      }
      
      
      // Refresh audit data once at the end
      await scanForAuditData();
      
    } catch (error) {
    }
    
    setAuditApplying(false);
  };
  
  // Move data from one field to another (e.g., eventSource → boxName)
  // Also moves related theme fields automatically!
  const applyAuditMove = async () => {
    if (!auditMoveModal || !auditMoveTargetField) return;
    
    setAuditApplying(true);
    
    try {
      const { fromField, value, itemIds } = auditMoveModal;
      const toField = auditMoveTargetField;
      
      // Define which fields have related theme fields
      const sourceToThemeMap: Record<string, string> = {
        eventSource: 'eventTheme',
        boxName: 'boxTheme',
        groupName: 'groupTheme',
        exclusiveTo: 'exclusiveTheme',
        charityName: 'charityCause',
        collabWith: 'collabTheme',
      };
      
      // Get the source's theme field (if any)
      const fromThemeField = sourceToThemeMap[fromField];
      // Get the target's theme field (if any)
      const toThemeField = sourceToThemeMap[toField];
      
      let batch = writeBatch(db);
      let batchCount = 0;
      let moved = 0;
      let themesMoved = 0;
      
      for (const itemPath of itemIds) {
        const itemRef = doc(db, itemPath);
        
        // Build update: copy to new field, clear old field
        const updates: Record<string, any> = {
          [toField]: value,    // Copy to target field
          [fromField]: '',     // Clear source field
        };
        
        // If both source and target have theme fields, move the theme too!
        if (fromThemeField && toThemeField) {
          // Need to read the document to get the current theme value
          const docSnap = await getDoc(itemRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            const themeValue = data[fromThemeField];
            if (themeValue) {
              updates[toThemeField] = themeValue;  // Copy theme to new field
              updates[fromThemeField] = '';        // Clear old theme field
              themesMoved++;
            }
          }
        } else if (fromThemeField && !toThemeField) {
          // Moving to a field without a theme - just clear the source theme
          updates[fromThemeField] = '';
        }
        
        // Set releaseType based on target field
        if (toField === 'boxName') {
          updates.releaseType = 'box';
        } else if (toField === 'eventSource' || toField === 'eventTheme') {
          updates.releaseType = 'event';
        } else if (toField === 'collectionName') {
          updates.releaseType = 'collection';
        } else if (toField === 'exclusiveTo') {
          updates.releaseType = 'exclusive';
        } else if (toField === 'charityName') {
          updates.releaseType = 'charity';
        } else if (toField === 'groupName') {
          updates.releaseType = 'groupCustom';
        } else if (toField === 'conventionName') {
          updates.releaseType = 'convention';
        }
        
        batch.update(itemRef, updates);
        batchCount++;
        moved++;
        
        if (batchCount >= 450) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }
      
      if (batchCount > 0) {
        await batch.commit();
      }
      
      
      // Refresh audit data
      await scanForAuditData();
      
    } catch (error) {
    }
    
    setAuditMoveModal(null);
    setAuditMoveTargetField('');
    setAuditApplying(false);
  };

  // ============================================
  // DATABASE SEARCH FUNCTIONS
  // ============================================
  
  const performSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearchLoading(true);
    setSearchPerformed(true);
    setSearchResults([]);
    
    try {
      const query_lower = searchQuery.toLowerCase().trim();
      const results: SearchResult[] = [];
      
      // Search across all items in the database
      const itemsQuery = collectionGroup(db, 'items');
      const snapshot = await getDocs(itemsQuery);
      
      // Fields to search in
      const searchableFields = [
        'name', 'brand', 'collectionName', 'eventSource', 'eventTheme',
        'boxName', 'boxTheme', 'groupName', 'groupTheme', 'exclusiveTo',
        'exclusiveTheme', 'charityName', 'charityCause', 'conventionName',
        'collabWith', 'collabTheme', 'collab', 'box', 'releaseType',
        'releaseNotes', 'colorDescription', 'barcode', 'storageLocation',
        'setName', 'setType'
      ];
      
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const matchedFields: { field: string; value: string }[] = [];
        
        // Check each searchable field
        for (const field of searchableFields) {
          const value = data[field];
          if (value && typeof value === 'string' && value.toLowerCase().includes(query_lower)) {
            matchedFields.push({ field, value });
          }
        }
        
        // Also check tags array
        if (data.tags && Array.isArray(data.tags)) {
          for (const tag of data.tags) {
            if (tag.toLowerCase().includes(query_lower)) {
              matchedFields.push({ field: 'tags', value: data.tags.join(', ') });
              break;
            }
          }
        }
        
        // If we found matches, add to results
        if (matchedFields.length > 0) {
          // Extract userId from path: users/{userId}/items/{itemId}
          const pathParts = docSnap.ref.path.split('/');
          const userId = pathParts[1];
          
          results.push({
            id: docSnap.id,
            docPath: docSnap.ref.path,
            userId,
            brand: data.brand || '',
            name: data.name || '',
            category: data.category || '',
            matchedFields,
            data,
          });
        }
      });
      
      // Sort by brand, then name
      results.sort((a, b) => {
        const brandCompare = a.brand.localeCompare(b.brand);
        if (brandCompare !== 0) return brandCompare;
        return a.name.localeCompare(b.name);
      });
      
      setSearchResults(results);
      
    } catch (error) {
    }
    
    setSearchLoading(false);
  };
  
  const openSearchEditModal = (result: SearchResult) => {
    setSearchEditModal(result);
    // Pre-populate edit data with current values
    // Tags is an array - convert to comma-separated string for editing
    const tagsArray = result.data.tags || [];
    const tagsString = Array.isArray(tagsArray) ? tagsArray.join(', ') : '';
    
    setSearchEditData({
      releaseType: result.data.releaseType || '',
      releaseYear: result.data.releaseYear || '',
      releaseSeason: result.data.releaseSeason || '',
      collectionName: result.data.collectionName || '',
      eventSource: result.data.eventSource || '',
      eventTheme: result.data.eventTheme || '',
      boxName: result.data.boxName || '',
      boxTheme: result.data.boxTheme || '',
      groupName: result.data.groupName || '',
      groupTheme: result.data.groupTheme || '',
      exclusiveTo: result.data.exclusiveTo || '',
      exclusiveTheme: result.data.exclusiveTheme || '',
      charityName: result.data.charityName || '',
      charityCause: result.data.charityCause || '',
      conventionName: result.data.conventionName || '',
      collabWith: result.data.collabWith || '',
      collabTheme: result.data.collabTheme || '',
      releaseNotes: result.data.releaseNotes || '',
      // Set/Duo fields
      setName: result.data.setName || '',
      setType: result.data.setType || '',
      // Tags (stored as array, edited as comma-separated string)
      tags: tagsString,
      // Legacy fields
      collab: result.data.collab || '',
      box: result.data.box || '',
    });
  };
  
  const saveSearchEdit = async () => {
    if (!searchEditModal) return;
    
    setSearchSaving(true);
    
    try {
      const docRef = doc(db, searchEditModal.docPath);
      
      // Build updates - only include non-empty values, clear empty ones
      const updates: Record<string, any> = {};
      
      for (const [key, value] of Object.entries(searchEditData)) {
        if (key === 'tags') {
          // Convert comma-separated tags string back to array
          const tagsString = (value as string).trim();
          if (tagsString) {
            updates[key] = tagsString.split(',').map(t => t.trim()).filter(t => t);
          } else {
            updates[key] = [];
          }
        } else if (typeof value === 'string') {
          updates[key] = value.trim() || '';
        } else {
          updates[key] = value;
        }
      }
      
      await updateDoc(docRef, updates);
      
      
      // Update the result in our local state
      setSearchResults(prev => prev.map(r => {
        if (r.id === searchEditModal.id) {
          return {
            ...r,
            data: { ...r.data, ...updates },
          };
        }
        return r;
      }));
      
      setSearchEditModal(null);
      setSearchEditData({});
      
    } catch (error) {
    }
    
    setSearchSaving(false);
  };

  // ============================================
  // FIX DATE-LIKE COLLECTION NAMES
  // ============================================
  // Detects patterns like "2024/09", "2023/Summer", "2024-02" in collectionName
  // Extracts year/season and clears the collectionName field
  
  const fixDateLikeCollectionNames = async () => {
    if (!auditData) return;
    
    setAuditApplying(true);
    setDateFixResults(null);
    
    // Month to season mapping
    const monthToSeason: Record<number, string> = {
      1: 'Winter', 2: 'Winter', 3: 'Spring',
      4: 'Spring', 5: 'Spring', 6: 'Summer',
      7: 'Summer', 8: 'Summer', 9: 'Fall',
      10: 'Fall', 11: 'Fall', 12: 'Winter'
    };
    
    // Month abbreviations to month number
    const monthAbbrevToNum: Record<string, number> = {
      'jan': 1, 'feb': 2, 'mar': 3, 'apr': 4, 'may': 5, 'jun': 6,
      'jul': 7, 'aug': 8, 'sep': 9, 'oct': 10, 'nov': 11, 'dec': 12,
      'january': 1, 'february': 2, 'march': 3, 'april': 4, 'june': 6,
      'july': 7, 'august': 8, 'september': 9, 'october': 10, 'november': 11, 'december': 12,
    };
    
    // Holiday keywords that map to seasons
    const holidayToSeason: Record<string, string> = {
      'holiday': 'Winter',
      'christmas': 'Winter',
      'halloween': 'Fall',
      'valentine': 'Winter',
      'valentines': 'Winter',
      'easter': 'Spring',
      'thanksgiving': 'Fall',
    };
    
    // Patterns to match date-only collection names
    // Each pattern has a 'type' to handle different capture group orders
    const datePatterns: { pattern: RegExp; type: 'year-first' | 'season-first' | 'year-only' | 'year-holiday' | 'year-range' | 'month-shortyear' }[] = [
      // YYYY/MM or YYYY-MM (e.g., 2024/09, 2024-02)
      { pattern: /^(20\d{2})[\/\-](\d{1,2})$/, type: 'year-first' },
      // YYYY/Season or YYYY-Season (e.g., 2023/Summer, 2024-Winter)
      { pattern: /^(20\d{2})[\/\-](Spring|Summer|Fall|Winter)$/i, type: 'year-first' },
      // YYYY Season (e.g., 2023 Summer)
      { pattern: /^(20\d{2})\s+(Spring|Summer|Fall|Winter)$/i, type: 'year-first' },
      // Season YYYY (e.g., Spring 2015, Summer 2016)
      { pattern: /^(Spring|Summer|Fall|Winter)\s+(20\d{2})$/i, type: 'season-first' },
      // YYYY/Holiday or YYYY-Holiday (e.g., 2013/Holiday, 2014/Christmas)
      { pattern: /^(20\d{2})[\/\-](Holiday|Christmas|Halloween|Valentine|Valentines|Easter|Thanksgiving)$/i, type: 'year-holiday' },
      // Year only (e.g., 2011, 2013, 2014)
      { pattern: /^(20\d{2})$/, type: 'year-only' },
      // Year range (e.g., 2016/2017) - takes first year, assumes Winter (holiday season)
      { pattern: /^(20\d{2})[\/\-](20\d{2})$/, type: 'year-range' },
      // Mon-YY or Month-YY (e.g., Aug-17, January-20) - assumes 20XX for the year
      { pattern: /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|January|February|March|April|June|July|August|September|October|November|December)[\/\-\s](\d{2})$/i, type: 'month-shortyear' },
    ];
    
    let found = 0;
    let fixed = 0;
    
    try {
      let batch = writeBatch(db);
      let batchCount = 0;
      
      // Go through all collectionName values
      for (const entry of auditData.collectionName) {
        const value = entry.value;
        let year: number | null = null;
        let season: string | null = null;
        let isDateOnly = false;
        
        // Check each pattern
        for (const { pattern, type } of datePatterns) {
          const match = value.match(pattern);
          if (match) {
            if (type === 'year-first') {
              year = parseInt(match[1]);
              const secondPart = match[2];
              
              // Check if it's a month number or season name
              const monthNum = parseInt(secondPart);
              if (!isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
                season = monthToSeason[monthNum];
              } else {
                // It's a season name - normalize capitalization
                season = secondPart.charAt(0).toUpperCase() + secondPart.slice(1).toLowerCase();
              }
            } else if (type === 'season-first') {
              // Season YYYY format
              season = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
              year = parseInt(match[2]);
            } else if (type === 'year-only') {
              // Just a year - no season
              year = parseInt(match[1]);
              season = null;
            } else if (type === 'year-holiday') {
              // Year with holiday keyword
              year = parseInt(match[1]);
              const holidayKey = match[2].toLowerCase();
              season = holidayToSeason[holidayKey] || 'Winter';
            } else if (type === 'year-range') {
              // Year range like 2016/2017 - take first year, assume Winter (holiday release)
              year = parseInt(match[1]);
              season = 'Winter';
            } else if (type === 'month-shortyear') {
              // Month-YY format like Aug-17, Jan-20
              const monthName = match[1].toLowerCase();
              const shortYear = parseInt(match[2]);
              const monthNum = monthAbbrevToNum[monthName];
              
              if (monthNum) {
                // Convert 2-digit year to 4-digit (assumes 2000s)
                year = 2000 + shortYear;
                season = monthToSeason[monthNum];
              }
            }
            
            isDateOnly = true;
            break;
          }
        }
        
        // For year-only, we still want to fix it (year is enough)
        if (isDateOnly && year) {
          found += entry.count;
          
          // Update all items with this value
          for (const itemId of entry.itemIds) {
            const itemRef = doc(db, itemId);
            
            const updates: Record<string, any> = {
              releaseYear: year,
              collectionName: '', // Clear the date-only value
            };
            
            // Only set season if we have one
            if (season) {
              updates.releaseSeason = season;
            }
            
            batch.update(itemRef, updates);
            
            batchCount++;
            fixed++;
            
            if (batchCount >= 450) {
              await batch.commit();
              batch = writeBatch(db);
              batchCount = 0;
            }
          }
        }
      }
      
      if (batchCount > 0) {
        await batch.commit();
      }
      
      setDateFixResults({ found, fixed });
      
      // Refresh the audit data
      if (fixed > 0) {
        await scanForAuditData();
      }
      
    } catch (error) {
    }
    
    setAuditApplying(false);
  };

  // ============================================
  // CLEAR JUNK/PLACEHOLDER VALUES
  // ============================================
  // Detects patterns like "????", "N/A", "unknown", "-", "TBD", etc.
  // Clears them from collectionName and sets releaseType to 'unknown' if empty
  
  const [junkFixResults, setJunkFixResults] = useState<{ found: number; fixed: number; examples: string[] } | null>(null);
  
  const clearJunkCollectionNames = async () => {
    if (!auditData) return;
    
    setAuditApplying(true);
    setJunkFixResults(null);
    
    // Patterns that indicate junk/placeholder values (case-insensitive)
    const junkPatterns = [
      /^\?+$/,                    // ???? or ??? or ?
      /^n\/?a$/i,                 // N/A, n/a, NA, na
      /^none$/i,                  // none, None, NONE
      /^unknown$/i,               // unknown, Unknown, UNKNOWN
      /^-+$/,                     // - or -- or ---
      /^tbd$/i,                   // TBD, tbd
      /^tba$/i,                   // TBA, tba
      /^\.+$/,                    // ... or .. or .
      /^x+$/i,                    // xxx, XX, x
      /^\(none\)$/i,              // (none)
      /^\[none\]$/i,              // [none]
      /^null$/i,                  // null
      /^undefined$/i,             // undefined
      /^#n\/a$/i,                 // #N/A (Excel error)
      /^#ref!?$/i,                // #REF! (Excel error)
      /^#value!?$/i,              // #VALUE! (Excel error)
      /^\s*$/,                    // whitespace only
    ];
    
    let found = 0;
    let fixed = 0;
    const examples: string[] = [];
    
    try {
      let batch = writeBatch(db);
      let batchCount = 0;
      
      // Go through all collectionName values
      for (const entry of auditData.collectionName) {
        const value = entry.value;
        let isJunk = false;
        
        // Check each junk pattern
        for (const pattern of junkPatterns) {
          if (pattern.test(value)) {
            isJunk = true;
            break;
          }
        }
        
        if (isJunk) {
          found += entry.count;
          if (examples.length < 5) {
            examples.push(`"${value}" (${entry.count} items)`);
          }
          
          // Update all items with this junk value
          for (const itemId of entry.itemIds) {
            const itemRef = doc(db, itemId);
            
            // Read the document to check current releaseType
            const docSnap = await getDoc(itemRef);
            const currentData = docSnap.exists() ? docSnap.data() : {};
            
            const updates: Record<string, any> = {
              collectionName: '', // Clear the junk value
            };
            
            // If releaseType is empty, set it to 'unknown'
            if (!currentData.releaseType || currentData.releaseType === '') {
              updates.releaseType = 'unknown';
            }
            
            batch.update(itemRef, updates);
            
            batchCount++;
            fixed++;
            
            if (batchCount >= 450) {
              await batch.commit();
              batch = writeBatch(db);
              batchCount = 0;
            }
          }
        }
      }
      
      if (batchCount > 0) {
        await batch.commit();
      }
      
      setJunkFixResults({ found, fixed, examples });
      
      // Refresh the audit data
      if (fixed > 0) {
        await scanForAuditData();
      }
      
    } catch (error) {
    }
    
    setAuditApplying(false);
  };

  // ============================================
  // APPLY FUNCTIONS
  // ============================================

  const applyBarcodeFixes = async () => {
    const selectedItems = barcodeItems.filter((item) => item.selected);
    if (selectedItems.length === 0) return;

    setIsApplying(true);

    try {
      let applied = 0;
      let batch = writeBatch(db);
      let batchCount = 0;

      for (const item of selectedItems) {
        const updates: any = {};
        if (item.suggested.name) updates.name = item.suggested.name;
        if (item.suggested.barcode) updates.barcode = item.suggested.barcode;

        batch.update(item.docRef, updates);
        batchCount++;
        applied++;

        if (batchCount >= 450) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }

      if (batchCount > 0) {
        await batch.commit();
      }

      // Auto-dismiss applied items so they don't show up again on next scan
      for (const item of selectedItems) {
        await persistBarcodeDismissal(item.id);
      }

      setAppliedCounts((prev) => ({ ...prev, barcode: prev.barcode + applied }));
      setBarcodeItems((prev) => prev.filter((item) => !item.selected));
    } catch (error) {
    }

    setIsApplying(false);
  };

  const applyWhitespaceFixes = async () => {
    const selectedItems = whitespaceItems.filter((item) => item.selected);
    if (selectedItems.length === 0) return;

    setIsApplying(true);

    try {
      let applied = 0;
      let batch = writeBatch(db);
      let batchCount = 0;

      for (const item of selectedItems) {
        const updates: any = {};
        if (item.suggested.name) updates.name = item.suggested.name;

        batch.update(item.docRef, updates);
        batchCount++;
        applied++;

        if (batchCount >= 450) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }

      if (batchCount > 0) {
        await batch.commit();
      }

      // Auto-dismiss applied items so they don't show up again on next scan
      for (const item of selectedItems) {
        await persistWhitespaceDismissal(item.id);
      }

      setAppliedCounts((prev) => ({ ...prev, whitespace: prev.whitespace + applied }));
      setWhitespaceItems((prev) => prev.filter((item) => !item.selected));
    } catch (error) {
    }

    setIsApplying(false);
  };

  const applyReleaseFixes = async () => {
    const selectedItems = releaseItems.filter((item) => item.selected);
    if (selectedItems.length === 0) return;

    setIsApplying(true);

    try {
      let applied = 0;
      let batch = writeBatch(db);
      let batchCount = 0;

      for (const item of selectedItems) {
        const updates: any = {};
        if (item.suggested.releaseType) updates.releaseType = item.suggested.releaseType;

        batch.update(item.docRef, updates);
        batchCount++;
        applied++;

        if (batchCount >= 450) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }

      if (batchCount > 0) {
        await batch.commit();
      }

      // Auto-dismiss applied items so they don't show up again on next scan
      for (const item of selectedItems) {
        await persistReleaseDismissal(item.id);
      }

      setAppliedCounts((prev) => ({ ...prev, release: prev.release + applied }));
      setReleaseItems((prev) => prev.filter((item) => !item.selected));
    } catch (error) {
    }

    setIsApplying(false);
  };

  // ============================================
  // APPLY PARSE COLLECTION - Now handles themes!
  // ============================================
  const applyParseCollectionFixes = async () => {
    const selectedItems = parseCollectionItems.filter((item) => item.selected);
    if (selectedItems.length === 0) return;

    setIsApplying(true);

    try {
      let applied = 0;
      let batch = writeBatch(db);
      let batchCount = 0;

      for (const item of selectedItems) {
        const updates: any = {};
        
        // Handle TAGS separately - they get saved to tags array and clear collectionName
        if (item.suggested.parseType === 'tag' && item.suggested.tags && item.suggested.tags.length > 0) {
          updates.tags = item.suggested.tags;
          updates.collectionName = '';  // Clear the collection name since it was actually a tag
          
          // If user also filled in other fields, save those too
          if (item.suggested.eventSource) {
            // Post-Jan 2026 rename — write canonical field + null legacy.
            updates.eventName = item.suggested.eventSource;
            updates.eventSource = null;
          }
          if (item.suggested.eventTheme) {
            updates.eventTheme = item.suggested.eventTheme;
          }
          if (item.suggested.collectionName) {
            updates.collectionName = item.suggested.collectionName;
          }
          if (item.suggested.releaseYear) {
            updates.releaseYear = item.suggested.releaseYear;
          }
          if (item.suggested.releaseSeason) {
            updates.releaseSeason = item.suggested.releaseSeason;
          }
          // New structured fields
          if (item.suggested.boxName) {
            updates.boxName = item.suggested.boxName;
          }
          if (item.suggested.boxTheme) {
            updates.boxTheme = item.suggested.boxTheme;
          }
          if (item.suggested.collabWith) {
            updates.collabWith = item.suggested.collabWith;
          }
          if (item.suggested.collabTheme) {
            updates.collabTheme = item.suggested.collabTheme;
          }
          if (item.suggested.groupName) {
            updates.groupName = item.suggested.groupName;
          }
          if (item.suggested.groupTheme) {
            updates.groupTheme = item.suggested.groupTheme;
          }
          if (item.suggested.exclusiveTo) {
            updates.exclusiveTo = item.suggested.exclusiveTo;
          }
          if (item.suggested.exclusiveTheme) {
            updates.exclusiveTheme = item.suggested.exclusiveTheme;
          }
          if (item.suggested.charityName) {
            updates.charityName = item.suggested.charityName;
          }
          if (item.suggested.charityCause) {
            updates.charityCause = item.suggested.charityCause;
          }
          if (item.suggested.conventionName) {
            updates.conventionName = item.suggested.conventionName;
          }
          // Re-release fields
          if (item.suggested.isRerelease !== undefined) {
            updates.isRerelease = item.suggested.isRerelease;
          }
          if (item.suggested.originalReleaseYear) {
            updates.originalReleaseYear = item.suggested.originalReleaseYear;
          }
          if (item.suggested.originalReleaseSeason) {
            updates.originalReleaseSeason = item.suggested.originalReleaseSeason;
          }
          if (item.suggested.releaseNotes) {
            updates.releaseNotes = item.suggested.releaseNotes;
          }
          // Category/Polish Type
          if (item.suggested.category) {
            updates.category = item.suggested.category;
          }
          // Legacy fields (for backwards compatibility)
          if (item.suggested.box) {
            updates.box = item.suggested.box;
          }
          if (item.suggested.collab) {
            updates.collab = item.suggested.collab;
          }
          
          batch.update(item.docRef, updates);
          batchCount++;
          applied++;
          
          if (batchCount >= 450) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          }
          continue;  // Skip the normal processing below
        }
        
        // For tags that have been edited, treat them based on what fields are filled
        const hasEvent = !!item.suggested.eventSource;
        const hasCollection = !!item.suggested.collectionName;
        
        // Determine effective type based on what's filled (allows editing tags)
        const effectiveType = item.suggested.parseType === 'tag'
          ? (hasEvent && hasCollection ? 'both' : hasEvent ? 'event' : hasCollection ? 'collection' : 'unknown')
          : item.suggested.parseType;
        
        if (effectiveType === 'event') {
          // Event type - could have eventTheme (PPU, HHC) or just event source
          if (item.suggested.eventSource) {
            // Post-Jan 2026 rename — write canonical field + null legacy.
            updates.eventName = item.suggested.eventSource;
            updates.eventSource = null;
          }
          // Theme-based events (PPU, HHC) get eventTheme, not collectionName
          if (item.suggested.eventTheme) {
            updates.eventTheme = item.suggested.eventTheme;
          }
          // Clear collection name since this is event data
          updates.collectionName = '';
          updates.releaseType = 'event';
        } else if (effectiveType === 'both') {
          // Both event AND collection
          if (item.suggested.collectionName) {
            updates.collectionName = item.suggested.collectionName;
          }
          if (item.suggested.eventSource) {
            // Post-Jan 2026 rename — write canonical field + null legacy.
            updates.eventName = item.suggested.eventSource;
            updates.eventSource = null;
          }
          if (item.suggested.eventTheme) {
            updates.eventTheme = item.suggested.eventTheme;
          }
          // Don't force releaseType for 'both'
        } else if (effectiveType === 'collection') {
          // Collection only
          if (item.suggested.collectionName) {
            updates.collectionName = item.suggested.collectionName;
          }
          updates.releaseType = 'collection';
        } else if (effectiveType === 'unknown') {
          // No meaningful data - skip
          continue;
        }
        
        // Set year and season if present
        if (item.suggested.releaseYear) {
          updates.releaseYear = item.suggested.releaseYear;
        }
        if (item.suggested.releaseSeason) {
          updates.releaseSeason = item.suggested.releaseSeason;
        }
        // New structured fields
        if (item.suggested.boxName) {
          updates.boxName = item.suggested.boxName;
        }
        if (item.suggested.boxTheme) {
          updates.boxTheme = item.suggested.boxTheme;
        }
        if (item.suggested.collabWith) {
          updates.collabWith = item.suggested.collabWith;
        }
        if (item.suggested.collabTheme) {
          updates.collabTheme = item.suggested.collabTheme;
        }
        if (item.suggested.groupName) {
          updates.groupName = item.suggested.groupName;
        }
        if (item.suggested.groupTheme) {
          updates.groupTheme = item.suggested.groupTheme;
        }
        if (item.suggested.exclusiveTo) {
          updates.exclusiveTo = item.suggested.exclusiveTo;
        }
        if (item.suggested.exclusiveTheme) {
          updates.exclusiveTheme = item.suggested.exclusiveTheme;
        }
        if (item.suggested.charityName) {
          updates.charityName = item.suggested.charityName;
        }
        if (item.suggested.charityCause) {
          updates.charityCause = item.suggested.charityCause;
        }
        if (item.suggested.conventionName) {
          updates.conventionName = item.suggested.conventionName;
        }
        // Re-release fields
        if (item.suggested.isRerelease !== undefined) {
          updates.isRerelease = item.suggested.isRerelease;
        }
        if (item.suggested.originalReleaseYear) {
          updates.originalReleaseYear = item.suggested.originalReleaseYear;
        }
        if (item.suggested.originalReleaseSeason) {
          updates.originalReleaseSeason = item.suggested.originalReleaseSeason;
        }
        if (item.suggested.releaseNotes) {
          updates.releaseNotes = item.suggested.releaseNotes;
        }
        // Category/Polish Type
        if (item.suggested.category) {
          updates.category = item.suggested.category;
        }
        // Legacy fields (for backwards compatibility)
        if (item.suggested.box) {
          updates.box = item.suggested.box;
        }
        if (item.suggested.collab) {
          updates.collab = item.suggested.collab;
        }
        // Set tags if present (available for ALL item types now)
        if (item.suggested.tags && item.suggested.tags.length > 0) {
          updates.tags = item.suggested.tags;
        }

        batch.update(item.docRef, updates);
        batchCount++;
        applied++;

        if (batchCount >= 450) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }

      if (batchCount > 0) {
        await batch.commit();
      }

      // Auto-dismiss applied items so they don't show up again on next scan
      for (const item of selectedItems) {
        await persistDismissal(item.id, item.original.collectionName || '');
      }

      setAppliedCounts((prev) => ({ ...prev, parseCollection: prev.parseCollection + applied }));
      setParseCollectionItems((prev) => prev.filter((item) => !item.selected));
    } catch (error) {
    }

    setIsApplying(false);
  };

  // ============================================
  // SELECTION HELPERS
  // ============================================

  const toggleItemSelection = (
    _items: CleanupItem[],
    setItems: React.Dispatch<React.SetStateAction<CleanupItem[]>>,
    itemId: string
  ) => {
    setItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, selected: !item.selected } : item))
    );
  };

  // Update a specific suggested field for a parse collection item
  const updateSuggestedField = (
    itemId: string,
    field: keyof CleanupItem['suggested'],
    value: string | number | boolean | string[] | undefined
  ) => {
    setParseCollectionItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? { ...item, suggested: { ...item.suggested, [field]: value } }
          : item
      )
    );
  };

  const selectAll = (setItems: React.Dispatch<React.SetStateAction<CleanupItem[]>>) => {
    setItems((prev) => prev.map((item) => ({ ...item, selected: true })));
  };

  const selectNone = (setItems: React.Dispatch<React.SetStateAction<CleanupItem[]>>) => {
    setItems((prev) => prev.map((item) => ({ ...item, selected: false })));
  };

  const toggleBrand = (brand: string) => {
    setExpandedBrands((prev) => {
      const next = new Set(prev);
      if (next.has(brand)) {
        next.delete(brand);
      } else {
        next.add(brand);
      }
      return next;
    });
  };

  // ============================================
  // GROUP BY BRAND
  // ============================================

  const groupByBrand = (items: CleanupItem[]): Map<string, CleanupItem[]> => {
    const grouped = new Map<string, CleanupItem[]>();
    items.forEach((item) => {
      const brand = item.brand;
      if (!grouped.has(brand)) {
        grouped.set(brand, []);
      }
      grouped.get(brand)!.push(item);
    });
    return grouped;
  };

  // ============================================
  // STEP NAVIGATION
  // ============================================

  const steps: { id: CleanupStep; label: string; icon: any }[] = [
    { id: 'barcode', label: 'Barcodes in Names', icon: Hash },
    { id: 'whitespace', label: 'Whitespace', icon: Type },
    { id: 'release', label: 'Release Type', icon: Calendar },
    { id: 'parseCollection', label: 'Parse Collections', icon: Sparkles },
    { id: 'destash', label: 'Destash Issues', icon: Store },
    { id: 'explore', label: 'Data Explorer', icon: Database },
    { id: 'search', label: 'Search Database', icon: Search },
    { id: 'audit', label: 'Data Audit', icon: ClipboardList },
    { id: 'summary', label: 'Summary', icon: CheckCircle },
  ];

  const currentStepIndex = steps.findIndex((s) => s.id === currentStep);

  const goToNextStep = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStep(steps[currentStepIndex + 1].id);
    }
  };

  const goToPrevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStep(steps[currentStepIndex - 1].id);
    }
  };

  // ============================================
  // RENDER
  // ============================================

  if (checkingAdmin) {
    return (
      <div className="max-w-6xl mx-auto p-8">
        <div className="glass-card p-6 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-purple-600" />
          <p>Checking admin status...</p>
        </div>
      </div>
    );
  }

  if (!currentUser || !isAdmin) {
    return (
      <div className="max-w-6xl mx-auto p-8">
        <div className="glass-card p-6 text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-red-400" />
          <h1 className="text-2xl font-bold text-glass mb-2 font-heading">Admin Access Required</h1>
          <p className="text-glass-secondary font-body">You must be logged in as an admin to view this page.</p>
        </div>
      </div>
    );
  }

  // Render item list grouped by brand
  const renderItemList = (
    items: CleanupItem[],
    _setItems: React.Dispatch<React.SetStateAction<CleanupItem[]>>,
    renderItem: (item: CleanupItem) => React.ReactNode
  ) => {
    const grouped = groupByBrand(items);
    const brands = Array.from(grouped.keys()).sort();

    return (
      <div className="space-y-2">
        {brands.map((brand) => {
          const brandItems = grouped.get(brand)!;
          const selectedCount = brandItems.filter((i) => i.selected).length;
          const isExpanded = expandedBrands.has(brand);

          return (
            <div key={brand} className="border border-slate-600 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleBrand(brand)}
                className="w-full flex items-center justify-between p-3 bg-slate-800/50 hover:bg-slate-700/50 transition-default"
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-glass-muted" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-glass-muted" />
                  )}
                  <span className="font-medium text-glass font-body">{brand}</span>
                  <span className="text-sm text-glass-muted font-body">
                    ({selectedCount}/{brandItems.length} selected)
                  </span>
                </div>
              </button>

              {isExpanded && (
                <div className="p-3 space-y-2 bg-slate-800/30">{brandItems.map((item) => renderItem(item))}</div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8">
      {/* Back Link */}
      <Link to="/admin" className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-4">
        <ArrowLeft className="w-4 h-4" />
        Back to Admin Dashboard
      </Link>

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl shadow-lg p-6 mb-6 text-white">
        <div className="flex items-center gap-3">
          <Sparkles className="w-10 h-10" />
          <div>
            <h1 className="text-2xl font-bold">Data Cleanup Wizard</h1>
            <p className="opacity-90">Fix common data quality issues step by step</p>
          </div>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="bg-white rounded-xl shadow p-4 mb-6">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = step.id === currentStep;
            const isPast = index < currentStepIndex;

            return (
              <button
                key={step.id}
                onClick={() => setCurrentStep(step.id)}
                className={`flex flex-col items-center gap-1 flex-1 py-2 transition-colors ${
                  isActive
                    ? 'text-purple-600'
                    : isPast
                    ? 'text-green-600'
                    : 'text-slate-400'
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isActive
                      ? 'bg-purple-100'
                      : isPast
                      ? 'bg-green-500/20'
                      : 'bg-slate-700/50'
                  }`}
                >
                  {isPast ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className="text-xs font-medium hidden md:block">{step.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="glass-card p-6 mb-6">
        {/* ============================================ */}
        {/* STEP: Barcode Extraction */}
        {/* ============================================ */}
        {currentStep === 'barcode' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-glass font-heading flex items-center gap-2">
                  <Hash className="w-6 h-6 text-purple-600" />
                  Extract Barcodes from Names
                </h2>
                <p className="text-glass-secondary text-sm mt-1">
                  Find items like "74 - Classic Horror" and move "74" to the barcode field
                </p>
              </div>

              <button
                onClick={scanForBarcodeInName}
                disabled={isScanning}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isScanning ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Scan
                  </>
                )}
              </button>
            </div>

            {barcodeItems.length > 0 && (
              <>
                <div className="flex items-center justify-between mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <span className="text-amber-300 font-medium">
                    Found {barcodeItems.length} items with barcodes in names
                    {(dismissedBarcodeItems.length > 0 || dismissedBarcodeIds.size > 0) && (
                      <span className="text-amber-600 text-sm ml-2">
                        ({dismissedBarcodeIds.size} dismissed)
                      </span>
                    )}
                  </span>
                  <div className="flex gap-2">
                    <button onClick={() => selectAll(setBarcodeItems)} className="text-sm text-purple-600 hover:underline">
                      Select All
                    </button>
                    <span className="text-gray-300">|</span>
                    <button onClick={() => selectNone(setBarcodeItems)} className="text-sm text-purple-600 hover:underline">
                      Select None
                    </button>
                  </div>
                </div>

                <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm text-blue-300">
                  <strong>Note:</strong> Some polish names may legitimately start with a number. 
                  Use the <X className="w-3 h-3 inline" /> button to dismiss false positives.
                </div>

                <div className="max-h-96 overflow-y-auto mb-4">
                  {renderItemList(barcodeItems, setBarcodeItems, (item) => (
                    <div
                      key={item.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border ${
                        item.selected ? 'border-purple-500/50 bg-purple-500/20' : 'border-slate-600 bg-slate-800/30'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={item.selected}
                        onChange={() => toggleItemSelection(barcodeItems, setBarcodeItems, item.id)}
                        className="mt-1 w-4 h-4 text-purple-600 rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-glass-muted font-medium">{item.brand}</span>
                        </div>
                        <div className="text-sm text-glass-muted line-through">{item.original.name}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm font-medium text-glass font-body">{item.suggested.name}</span>
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 border border-blue-500/30 rounded text-xs font-mono">
                            #{item.suggested.barcode}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          await persistBarcodeDismissal(item.id);
                          setDismissedBarcodeItems(prev => [...prev, item]);
                          setBarcodeItems(prev => prev.filter(i => i.id !== item.id));
                        }}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-500/100/20 rounded transition-colors"
                        title="Dismiss - this is not a barcode"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={async () => {
                      const selected = barcodeItems.filter(i => i.selected);
                      // Persist all dismissals to Firestore
                      for (const item of selected) {
                        await persistBarcodeDismissal(item.id);
                      }
                      setDismissedBarcodeItems(prev => [...prev, ...selected]);
                      setBarcodeItems(prev => prev.filter(i => !i.selected));
                    }}
                    disabled={barcodeItems.filter((i) => i.selected).length === 0}
                    className="px-4 py-3 border-2 border-gray-300 text-glass-secondary rounded-lg hover:bg-slate-700/50 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                  >
                    <X className="w-4 h-4" />
                    Dismiss Selected
                  </button>
                  
                  <button
                    onClick={applyBarcodeFixes}
                    disabled={isApplying || barcodeItems.filter((i) => i.selected).length === 0}
                    className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                  >
                    {isApplying ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Applying...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Apply {barcodeItems.filter((i) => i.selected).length} Fixes
                      </>
                    )}
                  </button>
                </div>
              </>
            )}

            {dismissedBarcodeItems.length > 0 && barcodeItems.length === 0 && (
              <div className="mt-4 p-4 bg-slate-800/30 border border-slate-600 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-glass-secondary font-medium">
                    {dismissedBarcodeItems.length} items dismissed as false positives
                  </span>
                  <button
                    onClick={() => {
                      setBarcodeItems(dismissedBarcodeItems);
                      setDismissedBarcodeItems([]);
                    }}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Restore All
                  </button>
                </div>
              </div>
            )}

            {barcodeItems.length === 0 && dismissedBarcodeItems.length === 0 && !isScanning && (
              <div className="text-center py-12 text-glass-muted">
                <Hash className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Click "Scan" to find items with barcodes in their names</p>
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* STEP: Whitespace */}
        {/* ============================================ */}
        {currentStep === 'whitespace' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-glass font-heading flex items-center gap-2">
                  <Type className="w-6 h-6 text-blue-600" />
                  Fix Whitespace Issues
                </h2>
                <p className="text-glass-secondary text-sm mt-1">Trim extra spaces from names and brands</p>
              </div>

              <button
                onClick={scanForWhitespace}
                disabled={isScanning}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isScanning ? <><RefreshCw className="w-4 h-4 animate-spin" />Scanning...</> : <><RefreshCw className="w-4 h-4" />Scan</>}
              </button>
            </div>

            {whitespaceItems.length > 0 && (
              <>
                <div className="flex items-center justify-between mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <span className="text-amber-300 font-medium">
                    Found {whitespaceItems.length} items with whitespace issues
                    {dismissedWhitespaceIds.size > 0 && (
                      <span className="text-amber-600 text-sm ml-2">
                        ({dismissedWhitespaceIds.size} dismissed)
                      </span>
                    )}
                  </span>
                  <div className="flex gap-2">
                    <button onClick={() => selectAll(setWhitespaceItems)} className="text-sm text-blue-600 hover:underline">Select All</button>
                    <span className="text-gray-300">|</span>
                    <button onClick={() => selectNone(setWhitespaceItems)} className="text-sm text-blue-600 hover:underline">Select None</button>
                  </div>
                </div>

                <div className="max-h-96 overflow-y-auto mb-4">
                  {renderItemList(whitespaceItems, setWhitespaceItems, (item) => (
                    <div key={item.id} className={`flex items-start gap-3 p-3 rounded-lg border ${item.selected ? 'border-blue-500/50 bg-blue-500/20' : 'border-slate-600 bg-slate-800/30'}`}>
                      <input type="checkbox" checked={item.selected} onChange={() => toggleItemSelection(whitespaceItems, setWhitespaceItems, item.id)} className="mt-1 w-4 h-4 text-blue-600 rounded" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-glass-muted">"<span className="bg-red-500/20 px-0.5">{item.original.name}</span>"</div>
                        <div className="text-sm font-medium text-glass font-body mt-1">→ "<span className="bg-green-500/20 px-0.5">{item.suggested.name}</span>"</div>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          await persistWhitespaceDismissal(item.id);
                          setWhitespaceItems(prev => prev.filter(i => i.id !== item.id));
                        }}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-500/100/20 rounded transition-colors"
                        title="Dismiss - keep original"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={async () => {
                      const selected = whitespaceItems.filter(i => i.selected);
                      for (const item of selected) {
                        await persistWhitespaceDismissal(item.id);
                      }
                      setWhitespaceItems(prev => prev.filter(i => !i.selected));
                    }}
                    disabled={whitespaceItems.filter((i) => i.selected).length === 0}
                    className="px-4 py-3 border-2 border-gray-300 text-glass-secondary rounded-lg hover:bg-slate-700/50 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                  >
                    <X className="w-4 h-4" />
                    Dismiss Selected
                  </button>
                  
                  <button onClick={applyWhitespaceFixes} disabled={isApplying || whitespaceItems.filter((i) => i.selected).length === 0} className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium">
                    {isApplying ? <><RefreshCw className="w-4 h-4 animate-spin" />Applying...</> : <><Check className="w-4 h-4" />Apply {whitespaceItems.filter((i) => i.selected).length} Fixes</>}
                  </button>
                </div>
              </>
            )}

            {whitespaceItems.length === 0 && !isScanning && (
              <div className="text-center py-12 text-glass-muted">
                <Type className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Click "Scan" to find items with whitespace issues</p>
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* STEP: Release Info */}
        {/* ============================================ */}
        {currentStep === 'release' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-glass font-heading flex items-center gap-2">
                  <Calendar className="w-6 h-6 text-green-600" />
                  Fix Release Info Mismatches
                </h2>
                <p className="text-glass-secondary text-sm mt-1">Correct items where release type doesn't match filled fields</p>
              </div>

              <button onClick={scanForReleaseMismatch} disabled={isScanning} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
                {isScanning ? <><RefreshCw className="w-4 h-4 animate-spin" />Scanning...</> : <><RefreshCw className="w-4 h-4" />Scan</>}
              </button>
            </div>

            {releaseItems.length > 0 && (
              <>
                <div className="flex items-center justify-between mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <span className="text-amber-300 font-medium">
                    Found {releaseItems.length} items with release info mismatches
                    {dismissedReleaseIds.size > 0 && (
                      <span className="text-amber-600 text-sm ml-2">
                        ({dismissedReleaseIds.size} dismissed)
                      </span>
                    )}
                  </span>
                  <div className="flex gap-2">
                    <button onClick={() => selectAll(setReleaseItems)} className="text-sm text-green-600 hover:underline">Select All</button>
                    <span className="text-gray-300">|</span>
                    <button onClick={() => selectNone(setReleaseItems)} className="text-sm text-green-600 hover:underline">Select None</button>
                  </div>
                </div>

                <div className="max-h-96 overflow-y-auto mb-4">
                  {renderItemList(releaseItems, setReleaseItems, (item) => (
                    <div key={item.id} className={`flex items-start gap-3 p-3 rounded-lg border ${item.selected ? 'border-green-500/50 bg-green-500/20' : 'border-slate-600 bg-slate-800/30'}`}>
                      <input type="checkbox" checked={item.selected} onChange={() => toggleItemSelection(releaseItems, setReleaseItems, item.id)} className="mt-1 w-4 h-4 text-green-600 rounded" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-glass font-body">{item.name}</div>
                        <div className="text-sm text-glass-muted mt-1">
                          Type: <span className="line-through text-red-600">{item.original.releaseType || 'empty'}</span>{' → '}<span className="text-green-600 font-medium">{item.suggested.releaseType}</span>
                        </div>
                        {item.original.eventSource && <div className="text-xs text-slate-400">Event: {item.original.eventSource}</div>}
                        {item.original.collectionName && <div className="text-xs text-slate-400">Collection: {item.original.collectionName}</div>}
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          await persistReleaseDismissal(item.id);
                          setReleaseItems(prev => prev.filter(i => i.id !== item.id));
                        }}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-500/100/20 rounded transition-colors"
                        title="Dismiss - keep original"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={async () => {
                      const selected = releaseItems.filter(i => i.selected);
                      for (const item of selected) {
                        await persistReleaseDismissal(item.id);
                      }
                      setReleaseItems(prev => prev.filter(i => !i.selected));
                    }}
                    disabled={releaseItems.filter((i) => i.selected).length === 0}
                    className="px-4 py-3 border-2 border-gray-300 text-glass-secondary rounded-lg hover:bg-slate-700/50 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                  >
                    <X className="w-4 h-4" />
                    Dismiss Selected
                  </button>
                  
                  <button onClick={applyReleaseFixes} disabled={isApplying || releaseItems.filter((i) => i.selected).length === 0} className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium">
                    {isApplying ? <><RefreshCw className="w-4 h-4 animate-spin" />Applying...</> : <><Check className="w-4 h-4" />Apply {releaseItems.filter((i) => i.selected).length} Fixes</>}
                  </button>
                </div>
              </>
            )}

            {releaseItems.length === 0 && !isScanning && (
              <div className="text-center py-12 text-glass-muted">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Click "Scan" to find release info mismatches</p>
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* STEP: Parse Collection Data - Now shows themes! */}
        {/* ============================================ */}
        {currentStep === 'parseCollection' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-glass font-heading flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-pink-600" />
                  Parse Collection Data
                </h2>
                <p className="text-glass-secondary text-sm mt-1">
                  Extract years, seasons, months, events, and themes from collection fields
                </p>
              </div>

              <button onClick={scanForParseableCollections} disabled={isScanning} className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50 flex items-center gap-2">
                {isScanning ? <><RefreshCw className="w-4 h-4 animate-spin" />Scanning...</> : <><RefreshCw className="w-4 h-4" />Scan</>}
              </button>
            </div>

            {parseCollectionItems.length > 0 && (
              <>
                {/* Legend - Updated for themes and tags */}
                <div className="mb-4 p-3 bg-slate-800/30 border border-slate-600 rounded-lg">
                  <div className="text-sm font-medium text-glass-secondary font-body mb-2">Parse Types:</div>
                  <div className="flex flex-wrap gap-3 text-xs">
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500"></span>Event (with theme for PPU/HHC)</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-purple-500"></span>Collection + Event</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500"></span>Collection only</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500"></span>Tag → saves to tags field, clears collection</span>
                  </div>
                  <div className="mt-2 text-xs text-glass-muted italic">
                    💡 All fields are editable. Use <X className="w-3 h-3 inline" /> to dismiss items that don't need changes (e.g., "Winter 2024" that's correct as-is).
                  </div>
                </div>

                <div className="flex items-center justify-between mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <span className="text-amber-300 font-medium">
                    Found {parseCollectionItems.length} items with parseable collection data
                    {dismissedParseCollectionIds.size > 0 && (
                      <span className="text-glass-muted ml-2">
                        ({dismissedParseCollectionIds.size} dismissed)
                        <button
                          onClick={async () => {
                            if (window.confirm(`Clear all ${dismissedParseCollectionIds.size} dismissals? They will reappear on the next scan.`)) {
                              // Clear all persisted dismissals from Firestore
                              const snapshot = await getDocs(collection(db, 'adminDismissals', 'parseCollection', 'items'));
                              for (const docSnap of snapshot.docs) {
                                await deleteDoc(docSnap.ref);
                              }
                              setDismissedParseCollectionIds(new Set());
                              setDismissedParseCollectionItems([]);
                            }
                          }}
                          className="ml-2 text-red-500 hover:underline hover:text-red-700"
                        >
                          Clear All
                        </button>
                      </span>
                    )}
                  </span>
                  <div className="flex gap-2">
                    <button onClick={() => selectAll(setParseCollectionItems)} className="text-sm text-pink-600 hover:underline">Select All</button>
                    <span className="text-gray-300">|</span>
                    <button 
                      onClick={() => setParseCollectionItems(prev => prev.map(item => 
                        item.suggested.parseType === 'tag' ? { ...item, selected: true } : item
                      ))} 
                      className="text-sm text-amber-600 hover:underline"
                    >
                      Select All Tags
                    </button>
                    <span className="text-gray-300">|</span>
                    <button onClick={() => selectNone(setParseCollectionItems)} className="text-sm text-pink-600 hover:underline">Select None</button>
                  </div>
                </div>

                <div className="max-h-[500px] overflow-y-auto mb-4 space-y-2">
                  {(() => {
                    const byUser = new Map<string, CleanupItem[]>();
                    parseCollectionItems.forEach(item => {
                      if (!byUser.has(item.userId)) byUser.set(item.userId, []);
                      byUser.get(item.userId)!.push(item);
                    });
                    
                    const selectAllForUser = (userId: string, select: boolean) => {
                      setParseCollectionItems(prev => prev.map(item => 
                        item.userId === userId
                          ? { ...item, selected: select }
                          : item
                      ));
                    };
                    
                    return Array.from(byUser.entries()).map(([userId, userItems]) => {
                      const selectedCount = userItems.filter(i => i.selected).length;
                      const isExpanded = expandedBrands.has(`user-${userId}`);
                      const allSelected = userItems.length > 0 && selectedCount === userItems.length;
                      const noneSelected = selectedCount === 0;
                      const tagCount = userItems.filter(i => i.suggested.parseType === 'tag').length;
                      
                      return (
                        <div key={userId} className="border border-slate-600 rounded-lg overflow-hidden">
                          <div className="flex items-center justify-between p-3 bg-slate-800/30">
                            <button onClick={() => toggleBrand(`user-${userId}`)} className="flex items-center gap-2 hover:bg-slate-700/50 rounded px-2 py-1 -ml-2">
                              {isExpanded ? <ChevronDown className="w-4 h-4 text-glass-muted" /> : <ChevronRight className="w-4 h-4 text-glass-muted" />}
                              <span className="font-medium text-glass font-body">User: {userId.slice(0, 8)}...</span>
                              <span className="text-sm text-glass-muted">
                                ({selectedCount}/{userItems.length} selected)
                                {tagCount > 0 && <span className="ml-1 text-amber-600">• {tagCount} tags</span>}
                              </span>
                            </button>
                            
                            <div className="flex items-center gap-2">
                              <button onClick={(e) => { e.stopPropagation(); selectAllForUser(userId, true); }} disabled={allSelected} className="text-xs text-pink-600 hover:underline disabled:opacity-50 disabled:no-underline">Select All</button>
                              <span className="text-gray-300">|</span>
                              <button onClick={(e) => { e.stopPropagation(); selectAllForUser(userId, false); }} disabled={noneSelected} className="text-xs text-pink-600 hover:underline disabled:opacity-50 disabled:no-underline">None</button>
                              <span className="text-gray-300">|</span>
                              <button 
                                onClick={async (e) => { 
                                  e.stopPropagation(); 
                                  const userItemsToMove = parseCollectionItems.filter(i => i.userId === userId);
                                  // Persist all dismissals to Firestore
                                  for (const item of userItemsToMove) {
                                    await persistDismissal(item.id, item.original.collectionName || '');
                                  }
                                  setDismissedParseCollectionItems(prev => [...prev, ...userItemsToMove]);
                                  setParseCollectionItems(prev => prev.filter(i => i.userId !== userId));
                                }} 
                                className="text-xs text-red-500 hover:underline"
                              >
                                Dismiss All
                              </button>
                            </div>
                          </div>
                          
                          {isExpanded && (
                            <div className="p-3 space-y-2 bg-white">
                              {userItems.map(item => {
                                const parseType = item.suggested.parseType;
                                const borderColor = 
                                  parseType === 'both' ? 'border-purple-500/50 bg-purple-500/20' :
                                  parseType === 'event' ? 'border-blue-500/50 bg-blue-500/20' :
                                  parseType === 'collection' ? 'border-green-500/50 bg-green-500/20' :
                                  parseType === 'tag' ? 'border-amber-300 bg-amber-50' :
                                  'border-slate-600 bg-slate-800/30';
                                
                                return (
                                  <div key={item.id} className={`p-3 rounded-lg border ${item.selected ? borderColor : 'border-slate-600 bg-slate-800/30'}`}>
                                    <div className="flex items-start gap-3">
                                      <input type="checkbox" checked={item.selected} onChange={() => toggleItemSelection(parseCollectionItems, setParseCollectionItems, item.id)} className="mt-1 w-4 h-4 text-pink-600 rounded" />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-2 mb-1">
                                          <div className="flex items-center gap-2">
                                            <span className="font-medium text-glass font-body">{item.brand}</span>
                                            <span className="text-slate-400">•</span>
                                            <span className="text-glass-secondary">{item.name}</span>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={async () => {
                                              await persistDismissal(item.id, item.original.collectionName || '');
                                              setDismissedParseCollectionItems(prev => [...prev, item]);
                                              setParseCollectionItems(prev => prev.filter(i => i.id !== item.id));
                                            }}
                                            className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-500/100/20 rounded transition-colors flex-shrink-0"
                                            title="Dismiss - keep original value"
                                          >
                                            <X className="w-4 h-4" />
                                          </button>
                                        </div>
                                        
                                        <div className="text-sm bg-red-500/10 p-2 rounded mb-2">
                                          <span className="text-red-600 font-medium">Original: </span>
                                          <span className="text-glass-secondary">"{item.original.collectionName}"</span>
                                        </div>
                                        
                                        <div className="text-sm bg-green-50 p-2 rounded">
                                          <div className="flex items-center justify-between mb-2">
                                            <span className="text-green-600 font-medium">Parsed (editable): </span>
                                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                              parseType === 'both' ? 'bg-purple-200 text-purple-800' :
                                              parseType === 'event' ? 'bg-blue-200 text-blue-300' :
                                              parseType === 'collection' ? 'bg-green-200 text-green-800' :
                                              parseType === 'tag' ? 'bg-amber-200 text-amber-300' :
                                              'bg-gray-200 text-glass'
                                            }`}>
                                              {parseType === 'both' ? 'COLLECTION + EVENT' : parseType?.toUpperCase()}
                                            </span>
                                          </div>
                                          
                                          <div className="space-y-3">
                                            {/* Polish Type / Category */}
                                            <div className="flex items-center gap-2">
                                              <label className="text-purple-600 font-medium w-24 text-xs">Polish Type:</label>
                                              <select
                                                value={item.suggested.category || ''}
                                                onChange={(e) => updateSuggestedField(item.id, 'category', e.target.value || undefined)}
                                                className="flex-1 px-2 py-1 text-sm border border-purple-300 rounded focus:border-purple-500 focus:ring-1 focus:ring-purple-500 bg-purple-50"
                                              >
                                                <option value="">Select type...</option>
                                                <option value="Base Coat">🛡️ Base Coat</option>
                                                <option value="Builder Gel">🏗️ Builder Gel</option>
                                                <option value="Dip Powder">🫧 Dip Powder</option>
                                                <option value="Gel Polish">✨ Gel Polish</option>
                                                <option value="Nail Lacquer">💅 Nail Lacquer</option>
                                                <option value="Poly Gel">🔮 Poly Gel</option>
                                                <option value="Stamping Polish">🎨 Stamping Polish</option>
                                                <option value="Top Coat">💎 Top Coat</option>
                                                <option value="Treatment">💊 Treatment</option>
                                              </select>
                                            </div>

                                            {/* Release Type Dropdown */}
                                            <div className="flex items-center gap-2">
                                              <label className="text-glass-secondary font-medium w-24 text-xs">Release Type:</label>
                                              <select
                                                value={item.suggested.releaseType || ''}
                                                onChange={(e) => updateSuggestedField(item.id, 'releaseType', e.target.value || undefined)}
                                                className="flex-1 px-2 py-1 text-sm border border-slate-600 rounded focus:border-gray-500 focus:ring-1 focus:ring-gray-500"
                                              >
                                                <option value="">Select release type...</option>
                                                <option value="unknown">❓ Unknown</option>
                                                <option value="core">🏠 Core / Permanent</option>
                                                <option value="collection">📚 Collection</option>
                                                <option value="event">🎪 Event (PPU, HHC, etc.)</option>
                                                <option value="box">📦 Subscription Box</option>
                                                <option value="groupCustom">👥 Group Custom</option>
                                                <option value="exclusive">⭐ Exclusive</option>
                                                <option value="charity">💝 Charity</option>
                                                <option value="limitedEdition">✨ Limited Edition</option>
                                                <option value="convention">🎫 Convention</option>
                                                <option value="other">📝 Other</option>
                                              </select>
                                            </div>

                                            {/* Conditional fields based on Release Type */}
                                            {item.suggested.releaseType === 'collection' && (
                                              <div className="p-2 bg-green-500/10 border border-green-500/30 rounded space-y-2">
                                                <div className="flex items-center gap-2">
                                                  <label className="text-green-600 font-medium w-24 text-xs">Collection:</label>
                                                  <input type="text" value={item.suggested.collectionName || ''} onChange={(e) => updateSuggestedField(item.id, 'collectionName', e.target.value || undefined)} placeholder="e.g., Enchanted Forest" className="flex-1 px-2 py-1 text-sm border border-green-300 rounded focus:border-green-500 focus:ring-1 focus:ring-green-500" />
                                                </div>
                                              </div>
                                            )}

                                            {item.suggested.releaseType === 'event' && (
                                              <div className="p-2 bg-amber-500/10 border border-amber-500/30 rounded space-y-2">
                                                <div className="flex items-center gap-2">
                                                  <label className="text-amber-600 font-medium w-24 text-xs">Event:</label>
                                                  <input type="text" value={item.suggested.eventSource || ''} onChange={(e) => updateSuggestedField(item.id, 'eventSource', e.target.value || undefined)} placeholder="e.g., Polish Pick Up, HHC" className="flex-1 px-2 py-1 text-sm border border-amber-300 rounded focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                  <label className="text-amber-500 font-medium w-24 text-xs">Event Theme:</label>
                                                  <input type="text" value={item.suggested.eventTheme || ''} onChange={(e) => updateSuggestedField(item.id, 'eventTheme', e.target.value || undefined)} placeholder="e.g., Enchanted Forest, March 2024" className="flex-1 px-2 py-1 text-sm border border-amber-200 rounded focus:border-amber-400 focus:ring-1 focus:ring-amber-400" />
                                                </div>
                                              </div>
                                            )}

                                            {item.suggested.releaseType === 'box' && (
                                              <div className="p-2 bg-orange-50 border border-orange-200 rounded space-y-2">
                                                <div className="flex items-center gap-2">
                                                  <label className="text-orange-600 font-medium w-24 text-xs">Box Name:</label>
                                                  <input type="text" value={item.suggested.boxName || item.suggested.box || ''} onChange={(e) => updateSuggestedField(item.id, 'boxName', e.target.value || undefined)} placeholder="e.g., Polished Gamers Box" className="flex-1 px-2 py-1 text-sm border border-orange-300 rounded focus:border-orange-500 focus:ring-1 focus:ring-orange-500" />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                  <label className="text-orange-500 font-medium w-24 text-xs">Box Theme:</label>
                                                  <input type="text" value={item.suggested.boxTheme || ''} onChange={(e) => updateSuggestedField(item.id, 'boxTheme', e.target.value || undefined)} placeholder="e.g., March 2024 - Spring" className="flex-1 px-2 py-1 text-sm border border-orange-200 rounded focus:border-orange-400 focus:ring-1 focus:ring-orange-400" />
                                                </div>
                                              </div>
                                            )}

                                            {item.suggested.releaseType === 'groupCustom' && (
                                              <div className="p-2 bg-cyan-50 border border-cyan-200 rounded space-y-2">
                                                <div className="flex items-center gap-2">
                                                  <label className="text-cyan-600 font-medium w-24 text-xs">Group:</label>
                                                  <input type="text" value={item.suggested.groupName || ''} onChange={(e) => updateSuggestedField(item.id, 'groupName', e.target.value || undefined)} placeholder="e.g., Reddit Lacqueristas" className="flex-1 px-2 py-1 text-sm border border-cyan-300 rounded focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500" />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                  <label className="text-cyan-500 font-medium w-24 text-xs">Group Theme:</label>
                                                  <input type="text" value={item.suggested.groupTheme || ''} onChange={(e) => updateSuggestedField(item.id, 'groupTheme', e.target.value || undefined)} placeholder="e.g., Anniversary Custom" className="flex-1 px-2 py-1 text-sm border border-cyan-200 rounded focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400" />
                                                </div>
                                              </div>
                                            )}

                                            {item.suggested.releaseType === 'exclusive' && (
                                              <div className="p-2 bg-yellow-50 border border-yellow-200 rounded space-y-2">
                                                <div className="flex items-center gap-2">
                                                  <label className="text-yellow-600 font-medium w-24 text-xs">Exclusive To:</label>
                                                  <input type="text" value={item.suggested.exclusiveTo || ''} onChange={(e) => updateSuggestedField(item.id, 'exclusiveTo', e.target.value || undefined)} placeholder="e.g., Color4Nails, Holo Taco" className="flex-1 px-2 py-1 text-sm border border-yellow-300 rounded focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500" />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                  <label className="text-yellow-500 font-medium w-24 text-xs">Excl. Theme:</label>
                                                  <input type="text" value={item.suggested.exclusiveTheme || ''} onChange={(e) => updateSuggestedField(item.id, 'exclusiveTheme', e.target.value || undefined)} placeholder="e.g., Black Friday 2024, Launch Exclusive" className="flex-1 px-2 py-1 text-sm border border-yellow-200 rounded focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400" />
                                                </div>
                                              </div>
                                            )}

                                            {item.suggested.releaseType === 'charity' && (
                                              <div className="p-2 bg-rose-50 border border-rose-200 rounded space-y-2">
                                                <div className="flex items-center gap-2">
                                                  <label className="text-rose-600 font-medium w-24 text-xs">Charity:</label>
                                                  <input type="text" value={item.suggested.charityName || ''} onChange={(e) => updateSuggestedField(item.id, 'charityName', e.target.value || undefined)} placeholder="e.g., Autism Speaks, Red Cross" className="flex-1 px-2 py-1 text-sm border border-rose-300 rounded focus:border-rose-500 focus:ring-1 focus:ring-rose-500" />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                  <label className="text-rose-500 font-medium w-24 text-xs">Cause:</label>
                                                  <input type="text" value={item.suggested.charityCause || ''} onChange={(e) => updateSuggestedField(item.id, 'charityCause', e.target.value || undefined)} placeholder="e.g., Autism Awareness, Cancer Research" className="flex-1 px-2 py-1 text-sm border border-rose-200 rounded focus:border-rose-400 focus:ring-1 focus:ring-rose-400" />
                                                </div>
                                              </div>
                                            )}

                                            {item.suggested.releaseType === 'convention' && (
                                              <div className="p-2 bg-teal-50 border border-teal-200 rounded space-y-2">
                                                <div className="flex items-center gap-2">
                                                  <label className="text-teal-600 font-medium w-24 text-xs">Convention:</label>
                                                  <input type="text" value={item.suggested.conventionName || ''} onChange={(e) => updateSuggestedField(item.id, 'conventionName', e.target.value || undefined)} placeholder="e.g., Polish Con, Indie Expo" className="flex-1 px-2 py-1 text-sm border border-teal-300 rounded focus:border-teal-500 focus:ring-1 focus:ring-teal-500" />
                                                </div>
                                              </div>
                                            )}

                                            {/* Divider */}
                                            <div className="border-t border-slate-600 pt-2 mt-2"></div>

                                            {/* Always visible: Year & Season */}
                                            <div className="flex items-center gap-2">
                                              <label className="text-glass-secondary font-medium w-24 text-xs">Year:</label>
                                              <input type="number" value={item.suggested.releaseYear || ''} onChange={(e) => updateSuggestedField(item.id, 'releaseYear', e.target.value ? parseInt(e.target.value) : undefined)} placeholder="2024" min="2010" max="2030" className="w-20 px-2 py-1 text-sm border border-slate-600 rounded focus:border-gray-500 focus:ring-1 focus:ring-gray-500" />
                                              <label className="text-glass-secondary font-medium w-16 text-xs ml-2">Season:</label>
                                              <select value={item.suggested.releaseSeason || ''} onChange={(e) => updateSuggestedField(item.id, 'releaseSeason', e.target.value || undefined)} className="px-2 py-1 text-sm border border-slate-600 rounded focus:border-gray-500 focus:ring-1 focus:ring-gray-500">
                                                <option value="">--</option>
                                                <option value="Spring">🌷 Spring</option>
                                                <option value="Summer">☀️ Summer</option>
                                                <option value="Fall">🍂 Fall</option>
                                                <option value="Winter">❄️ Winter</option>
                                              </select>
                                            </div>

                                            {/* Always visible: Collab */}
                                            <div className="p-2 bg-violet-50 border border-violet-200 rounded space-y-2">
                                              <div className="flex items-center gap-2">
                                                <label className="text-violet-600 font-medium w-24 text-xs">Collab With:</label>
                                                <input type="text" value={item.suggested.collabWith || item.suggested.collab || ''} onChange={(e) => updateSuggestedField(item.id, 'collabWith', e.target.value || undefined)} placeholder="e.g., @gotnail, Simply Nailogical" className="flex-1 px-2 py-1 text-sm border border-violet-300 rounded focus:border-violet-500 focus:ring-1 focus:ring-violet-500" />
                                              </div>
                                              <div className="flex items-center gap-2">
                                                <label className="text-violet-500 font-medium w-24 text-xs">Collab Theme:</label>
                                                <input type="text" value={item.suggested.collabTheme || ''} onChange={(e) => updateSuggestedField(item.id, 'collabTheme', e.target.value || undefined)} placeholder="e.g., Halloween 2024" className="flex-1 px-2 py-1 text-sm border border-violet-200 rounded focus:border-violet-400 focus:ring-1 focus:ring-violet-400" />
                                              </div>
                                            </div>
                                            
                                            {/* Re-release Section */}
                                            <div className="border border-amber-200 rounded-lg p-2 bg-amber-50/50">
                                              <div className="flex items-center gap-2 mb-2">
                                                <input
                                                  type="checkbox"
                                                  id={`rerelease-${item.id}`}
                                                  checked={item.suggested.isRerelease || false}
                                                  onChange={(e) => updateSuggestedField(item.id, 'isRerelease', e.target.checked ? true : undefined)}
                                                  className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                                                />
                                                <label htmlFor={`rerelease-${item.id}`} className="text-amber-700 font-medium text-xs">
                                                  🔄 This is a re-release of an earlier polish
                                                </label>
                                              </div>
                                              
                                              {item.suggested.isRerelease && (
                                                <div className="grid grid-cols-2 gap-2 ml-6">
                                                  <div className="flex items-center gap-2">
                                                    <label className="text-amber-600 font-medium text-xs whitespace-nowrap">Original Year:</label>
                                                    <input
                                                      type="number"
                                                      min="2010"
                                                      max="2030"
                                                      value={item.suggested.originalReleaseYear || ''}
                                                      onChange={(e) => updateSuggestedField(item.id, 'originalReleaseYear', e.target.value ? parseInt(e.target.value) : undefined)}
                                                      placeholder="2019"
                                                      className="w-20 px-2 py-1 text-sm border border-amber-300 rounded focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                                                    />
                                                  </div>
                                                  <div className="flex items-center gap-2">
                                                    <label className="text-amber-600 font-medium text-xs whitespace-nowrap">Original Season:</label>
                                                    <select
                                                      value={item.suggested.originalReleaseSeason || ''}
                                                      onChange={(e) => updateSuggestedField(item.id, 'originalReleaseSeason', e.target.value || undefined)}
                                                      className="flex-1 px-2 py-1 text-sm border border-amber-300 rounded focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                                                    >
                                                      <option value="">Select...</option>
                                                      <option value="Spring">🌷 Spring</option>
                                                      <option value="Summer">☀️ Summer</option>
                                                      <option value="Fall">🍂 Fall</option>
                                                      <option value="Winter">❄️ Winter</option>
                                                    </select>
                                                  </div>
                                                </div>
                                              )}
                                              
                                              <div className="flex items-center gap-2 mt-2">
                                                <label className="text-amber-500 font-medium w-24 text-xs">Release Notes:</label>
                                                <input
                                                  type="text"
                                                  value={item.suggested.releaseNotes || ''}
                                                  onChange={(e) => updateSuggestedField(item.id, 'releaseNotes', e.target.value || undefined)}
                                                  placeholder="e.g., New label, tweaked formula"
                                                  className="flex-1 px-2 py-1 text-sm border border-amber-200 rounded focus:border-amber-400 focus:ring-1 focus:ring-amber-400"
                                                />
                                              </div>
                                            </div>
                                            
                                            {/* Tags field */}
                                            <div className="flex items-center gap-2">
                                              <label className="text-amber-600 font-medium w-24 text-xs">Tags:</label>
                                              <input
                                                type="text"
                                                defaultValue={(item.suggested.tags || []).join(', ')}
                                                onBlur={(e) => {
                                                  const newTags = e.target.value
                                                    .split(',')
                                                    .map(t => t.trim())
                                                    .filter(t => t.length > 0);
                                                  updateSuggestedField(item.id, 'tags', newTags.length > 0 ? newTags : undefined);
                                                }}
                                                placeholder="e.g., Christmas, Plaid, Santa"
                                                className="flex-1 px-2 py-1 text-sm border border-amber-300 rounded focus:border-amber-500 focus:ring-1 focus:ring-amber-500 bg-amber-50"
                                              />
                                            </div>
                                            
                                            <div className="text-glass-muted italic text-xs mt-2">
                                              💡 Select a release type to show relevant fields. Year, Season, Collab & Tags are always available.
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={async () => {
                      const selected = parseCollectionItems.filter(i => i.selected);
                      // Persist all dismissals to Firestore
                      for (const item of selected) {
                        await persistDismissal(item.id, item.original.collectionName || '');
                      }
                      setDismissedParseCollectionItems(prev => [...prev, ...selected]);
                      setParseCollectionItems(prev => prev.filter(i => !i.selected));
                    }}
                    disabled={parseCollectionItems.filter((i) => i.selected).length === 0}
                    className="px-4 py-3 border-2 border-gray-300 text-glass-secondary rounded-lg hover:bg-slate-700/50 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                  >
                    <X className="w-4 h-4" />
                    Dismiss Selected
                  </button>
                  
                  <button onClick={applyParseCollectionFixes} disabled={isApplying || parseCollectionItems.filter((i) => i.selected).length === 0} className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium">
                    {isApplying ? <><RefreshCw className="w-4 h-4 animate-spin" />Applying...</> : <><Check className="w-4 h-4" />Apply {parseCollectionItems.filter((i) => i.selected).length} Fixes</>}
                  </button>
                </div>
              </>
            )}

            {dismissedParseCollectionItems.length > 0 && parseCollectionItems.length === 0 && (
              <div className="mt-4 p-4 bg-slate-800/30 border border-slate-600 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-glass-secondary font-medium">
                    {dismissedParseCollectionItems.length} items dismissed (keeping original values)
                    {dismissedParseCollectionIds.size > dismissedParseCollectionItems.length && (
                      <span className="text-slate-400 ml-1">
                        ({dismissedParseCollectionIds.size} total persisted)
                      </span>
                    )}
                  </span>
                  <button
                    onClick={async () => {
                      // Remove all dismissals from Firestore
                      for (const item of dismissedParseCollectionItems) {
                        await removeDismissal(item.id);
                      }
                      setParseCollectionItems(dismissedParseCollectionItems);
                      setDismissedParseCollectionItems([]);
                    }}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Restore All
                  </button>
                </div>
              </div>
            )}

            {parseCollectionItems.length === 0 && dismissedParseCollectionItems.length === 0 && !isScanning && (
              <div className="text-center py-12 text-glass-muted">
                <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Click "Scan" to find collection fields that can be parsed</p>
                {dismissedParseCollectionIds.size > 0 && (
                  <div className="mt-4">
                    <p className="text-sm text-slate-400 mb-2">
                      ({dismissedParseCollectionIds.size} items previously dismissed)
                    </p>
                    <button
                      onClick={async () => {
                        // Clear all persisted dismissals from Firestore
                        const snapshot = await getDocs(collection(db, 'adminDismissals', 'parseCollection', 'items'));
                        for (const docSnap of snapshot.docs) {
                          await deleteDoc(docSnap.ref);
                        }
                        setDismissedParseCollectionIds(new Set());
                      }}
                      className="text-sm text-red-500 hover:underline"
                    >
                      Clear All Dismissals (re-scan will show them again)
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* STEP: Destash Issues */}
        {/* ============================================ */}
        {currentStep === 'destash' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-glass font-heading flex items-center gap-2">
                  <Store className="w-6 h-6 text-red-600" />
                  Destash/Marketplace Issues
                </h2>
                <p className="text-glass-secondary text-sm mt-1">Find listings missing required fields (requires manual fix by user)</p>
              </div>

              <button onClick={scanForDestashIssues} disabled={isScanning} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
                {isScanning ? <><RefreshCw className="w-4 h-4 animate-spin" />Scanning...</> : <><RefreshCw className="w-4 h-4" />Scan</>}
              </button>
            </div>

            {destashItems.length > 0 && (
              <>
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4">
                  <span className="text-amber-300 font-medium">
                    Found {destashItems.length} listings with issues
                    {dismissedDestashIds.size > 0 && (
                      <span className="text-amber-600 text-sm ml-2">
                        ({dismissedDestashIds.size} dismissed)
                      </span>
                    )}
                  </span>
                  <p className="text-amber-700 text-sm mt-1">⚠️ These need manual review — users should fix their own listings</p>
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {renderItemList(destashItems, setDestashItems, (item) => (
                    <div key={item.id} className="p-3 rounded-lg border border-red-200 bg-red-500/10 flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-glass font-body">{item.name}</div>
                        <div className="text-sm text-red-600 mt-1"><AlertTriangle className="w-3 h-3 inline mr-1" />Issues: {item.original.name}</div>
                        <div className="text-xs text-slate-400 mt-1">User: {item.userId.slice(0, 8)}...</div>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          await persistDestashDismissal(item.id);
                          setDestashItems(prev => prev.filter(i => i.id !== item.id));
                        }}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-500/20 rounded transition-colors"
                        title="Dismiss - acknowledged"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {destashItems.length === 0 && !isScanning && (
              <div className="text-center py-12 text-glass-muted">
                <Store className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Click "Scan" to find destash listing issues</p>
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* STEP: Data Explorer */}
        {/* ============================================ */}
        {currentStep === 'explore' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-glass font-heading flex items-center gap-2">
                  <Database className="w-6 h-6 text-indigo-600" />
                  Data Explorer
                </h2>
                <p className="text-glass-secondary text-sm mt-1">Browse and edit unique values across your database</p>
              </div>
            </div>

            {/* Field selector */}
            <div className="space-y-2 mb-4">
              {/* Collab fields */}
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-glass-muted w-full mb-1">Collaborations:</span>
                {[
                  { id: 'collabWith' as ExplorerField, label: 'Collab With' },
                  { id: 'collabTheme' as ExplorerField, label: 'Collab Theme' },
                  { id: 'collab' as ExplorerField, label: 'Collab (Legacy)' },
                ].map((field) => (
                  <button
                    key={field.id}
                    onClick={() => {
                      setExplorerField(field.id);
                      scanForExplorerField(field.id);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      explorerField === field.id && explorerData.length > 0
                        ? 'bg-violet-600 text-white'
                        : 'bg-violet-50 text-violet-700 hover:bg-violet-100'
                    }`}
                  >
                    {field.label}
                  </button>
                ))}
              </div>
              
              {/* Event fields */}
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-glass-muted w-full mb-1">Events:</span>
                {[
                  { id: 'eventSource' as ExplorerField, label: 'Event Source' },
                  { id: 'eventName' as ExplorerField, label: 'Event Name' },
                  { id: 'eventTheme' as ExplorerField, label: 'Event Theme' },
                ].map((field) => (
                  <button
                    key={field.id}
                    onClick={() => {
                      setExplorerField(field.id);
                      scanForExplorerField(field.id);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      explorerField === field.id && explorerData.length > 0
                        ? 'bg-amber-600 text-white'
                        : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                    }`}
                  >
                    {field.label}
                  </button>
                ))}
              </div>
              
              {/* Box fields */}
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-glass-muted w-full mb-1">Subscription Boxes:</span>
                {[
                  { id: 'box' as ExplorerField, label: 'Box (Legacy)' },
                  { id: 'boxName' as ExplorerField, label: 'Box Name' },
                  { id: 'boxTheme' as ExplorerField, label: 'Box Theme' },
                ].map((field) => (
                  <button
                    key={field.id}
                    onClick={() => {
                      setExplorerField(field.id);
                      scanForExplorerField(field.id);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      explorerField === field.id && explorerData.length > 0
                        ? 'bg-orange-600 text-white'
                        : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
                    }`}
                  >
                    {field.label}
                  </button>
                ))}
              </div>
              
              {/* Group Custom fields */}
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-glass-muted w-full mb-1">Group Customs:</span>
                {[
                  { id: 'groupName' as ExplorerField, label: 'Group Name' },
                  { id: 'groupTheme' as ExplorerField, label: 'Group Theme' },
                ].map((field) => (
                  <button
                    key={field.id}
                    onClick={() => {
                      setExplorerField(field.id);
                      scanForExplorerField(field.id);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      explorerField === field.id && explorerData.length > 0
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                    }`}
                  >
                    {field.label}
                  </button>
                ))}
              </div>
              
              {/* Charity fields */}
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-glass-muted w-full mb-1">Charity:</span>
                {[
                  { id: 'charityName' as ExplorerField, label: 'Charity Name' },
                  { id: 'charityCause' as ExplorerField, label: 'Charity Cause' },
                ].map((field) => (
                  <button
                    key={field.id}
                    onClick={() => {
                      setExplorerField(field.id);
                      scanForExplorerField(field.id);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      explorerField === field.id && explorerData.length > 0
                        ? 'bg-rose-600 text-white'
                        : 'bg-rose-50 text-rose-700 hover:bg-rose-100'
                    }`}
                  >
                    {field.label}
                  </button>
                ))}
              </div>
              
              {/* Other fields */}
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-glass-muted w-full mb-1">Other:</span>
                {[
                  { id: 'collectionName' as ExplorerField, label: 'Collections' },
                  { id: 'exclusiveTo' as ExplorerField, label: 'Exclusive To' },
                  { id: 'exclusiveTheme' as ExplorerField, label: 'Exclusive Theme' },
                  { id: 'conventionName' as ExplorerField, label: 'Conventions' },
                  { id: 'category' as ExplorerField, label: 'Polish Type' },
                  { id: 'tags' as ExplorerField, label: 'Tags' },
                  { id: 'brand' as ExplorerField, label: 'Brands' },
                ].map((field) => (
                  <button
                    key={field.id}
                    onClick={() => {
                      setExplorerField(field.id);
                      scanForExplorerField(field.id);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      explorerField === field.id && explorerData.length > 0
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-700/50 text-glass-secondary hover:bg-slate-600'
                    }`}
                  >
                    {field.label}
                  </button>
                ))}
              </div>
            </div>

            {explorerLoading && (
              <div className="text-center py-12 text-glass-muted">
                <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin text-indigo-600" />
                <p>Scanning database...</p>
              </div>
            )}

            {!explorerLoading && explorerData.length > 0 && (
              <>
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg mb-4">
                  <span className="text-indigo-800 font-medium">
                    Found {explorerData.length} unique values
                  </span>
                  <span className="text-indigo-600 text-sm ml-2">
                    (click to expand, pencil to edit)
                  </span>
                </div>

                <div className="max-h-[500px] overflow-y-auto space-y-2">
                  {explorerData.map((entry) => (
                    <div key={entry.value} className="border border-slate-600 rounded-lg overflow-hidden">
                      {/* Header row */}
                      <div 
                        className="flex items-center gap-3 p-3 bg-slate-800/30 cursor-pointer hover:bg-slate-700/50"
                        onClick={() => {
                          setExpandedValues(prev => {
                            const next = new Set(prev);
                            if (next.has(entry.value)) {
                              next.delete(entry.value);
                            } else {
                              next.add(entry.value);
                            }
                            return next;
                          });
                        }}
                      >
                        {expandedValues.has(entry.value) ? (
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        )}
                        
                        <div className="flex-1 min-w-0">
                          {editingValue === entry.value ? (
                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="text"
                                value={editNewValue}
                                onChange={(e) => setEditNewValue(e.target.value)}
                                className="flex-1 px-2 py-1 text-sm border border-indigo-300 rounded focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    applyExplorerEdit(entry.value, editNewValue, explorerField);
                                  } else if (e.key === 'Escape') {
                                    setEditingValue(null);
                                    setEditNewValue('');
                                  }
                                }}
                              />
                              <button
                                onClick={() => applyExplorerEdit(entry.value, editNewValue, explorerField)}
                                disabled={isApplying}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                                title="Save"
                              >
                                {isApplying ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => {
                                  setEditingValue(null);
                                  setEditNewValue('');
                                }}
                                className="p-1.5 text-slate-400 hover:bg-slate-700/50 rounded"
                                title="Cancel"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <span className="font-medium text-glass font-body">{entry.value}</span>
                          )}
                        </div>
                        
                        <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-sm font-medium">
                          {entry.count} items
                        </span>
                        
                        {editingValue !== entry.value && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingValue(entry.value);
                              setEditNewValue(entry.value);
                            }}
                            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                            title="Edit this value across all items"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      {/* Expanded items list */}
                      {expandedValues.has(entry.value) && (
                        <div className="border-t border-slate-600 bg-white p-3 max-h-60 overflow-y-auto">
                          <div className="space-y-1">
                            {entry.items.slice(0, 50).map((item) => (
                              <div key={item.id} className="text-sm text-glass-secondary flex items-center gap-2">
                                <span className="text-slate-400">•</span>
                                <span className="font-medium text-glass-secondary">{item.brand}</span>
                                <span className="text-slate-400">-</span>
                                <span>{item.name}</span>
                              </div>
                            ))}
                            {entry.items.length > 50 && (
                              <div className="text-sm text-slate-400 italic mt-2">
                                ... and {entry.items.length - 50} more items
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {!explorerLoading && explorerData.length === 0 && (
              <div className="text-center py-12 text-glass-muted">
                <Database className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Select a field above to explore your data</p>
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* STEP: Search Database */}
        {/* ============================================ */}
        {currentStep === 'search' && (
          <div>
            <h2 className="text-xl font-bold text-glass font-heading flex items-center gap-2 mb-2">
              <Search className="w-6 h-6 text-blue-600" />
              Search Entire Database
            </h2>
            <p className="text-glass-secondary text-sm mb-6">Search across ALL fields in your database to find items containing specific text.</p>

            {/* Search Input */}
            <div className="flex gap-2 mb-6">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && performSearch()}
                placeholder="Search for: charity, exclusive, group custom, retailer name..."
                className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              />
              <button
                onClick={performSearch}
                disabled={searchLoading || !searchQuery.trim()}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {searchLoading ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    Search
                  </>
                )}
              </button>
            </div>

            {/* Search Results */}
            {searchPerformed && !searchLoading && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-glass-secondary">
                    Found <span className="font-bold text-blue-600">{searchResults.length}</span> items containing "{searchQuery}"
                  </p>
                </div>

                {searchResults.length === 0 ? (
                  <div className="text-center py-12 text-glass-muted">
                    <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No items found matching "{searchQuery}"</p>
                    <p className="text-sm mt-1">Try different keywords or check spelling</p>
                  </div>
                ) : (
                  <div className="border border-slate-600 rounded-lg overflow-hidden">
                    <div className="max-h-[500px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-700/50 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium text-glass-secondary">Brand</th>
                            <th className="text-left px-3 py-2 font-medium text-glass-secondary">Name</th>
                            <th className="text-left px-3 py-2 font-medium text-glass-secondary">Matched In</th>
                            <th className="text-right px-3 py-2 font-medium text-glass-secondary w-20">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {searchResults.map((result) => (
                            <tr key={result.id} className="border-b border-slate-700 hover:bg-blue-50">
                              <td className="px-3 py-2 text-gray-900 font-medium">{result.brand}</td>
                              <td className="px-3 py-2 text-glass-secondary">{result.name}</td>
                              <td className="px-3 py-2">
                                <div className="flex flex-wrap gap-1">
                                  {result.matchedFields.map((match, idx) => (
                                    <span
                                      key={idx}
                                      className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800"
                                      title={match.value}
                                    >
                                      <span className="font-medium">{match.field}:</span>
                                      <span className="ml-1 max-w-32 truncate">{match.value}</span>
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td className="px-3 py-2 text-right">
                                <button
                                  onClick={() => openSearchEditModal(result)}
                                  className="text-blue-600 hover:underline flex items-center gap-1 ml-auto"
                                >
                                  <Pencil className="w-3 h-3" />
                                  Edit
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Edit Modal */}
            {searchEditModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
                  <h3 className="text-lg font-bold text-glass font-heading mb-2">
                    Edit Item
                  </h3>
                  <p className="text-sm text-glass-secondary mb-4">
                    {searchEditModal.brand} • {searchEditModal.name}
                  </p>
                  
                  <div className="space-y-4">
                    {/* Release Type + Year/Season Row */}
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-glass-secondary font-body mb-1">Release Type</label>
                        <select
                          value={searchEditData.releaseType || ''}
                          onChange={(e) => setSearchEditData(prev => ({ ...prev, releaseType: e.target.value }))}
                          className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select...</option>
                          <option value="core">🏠 Core / Permanent</option>
                          <option value="collection">📚 Collection</option>
                          <option value="event">🎪 Event (PPU, HHC, etc.)</option>
                          <option value="box">📦 Subscription Box</option>
                          <option value="exclusive">⭐ Exclusive</option>
                          <option value="charity">💝 Charity</option>
                          <option value="groupCustom">👥 Group Custom</option>
                          <option value="convention">🎫 Convention</option>
                          <option value="collab">🤝 Collab</option>
                          <option value="unknown">❓ Unknown</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-1">📅 Year</label>
                        <input
                          type="number"
                          min="2010"
                          max="2030"
                          value={searchEditData.releaseYear || ''}
                          onChange={(e) => setSearchEditData(prev => ({ ...prev, releaseYear: e.target.value ? parseInt(e.target.value) : '' }))}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg bg-blue-50"
                          placeholder="2019"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-1">🗓️ Season</label>
                        <select
                          value={searchEditData.releaseSeason || ''}
                          onChange={(e) => setSearchEditData(prev => ({ ...prev, releaseSeason: e.target.value }))}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg bg-blue-50"
                        >
                          <option value="">Select...</option>
                          <option value="Spring">🌷 Spring</option>
                          <option value="Summer">☀️ Summer</option>
                          <option value="Fall">🍂 Fall</option>
                          <option value="Winter">❄️ Winter</option>
                        </select>
                      </div>
                    </div>

                    {/* Collection */}
                    <div className="border border-green-200 rounded-lg p-3 bg-green-50/30">
                      <label className="block text-sm font-medium text-green-700 mb-2">📚 Collection</label>
                      <input
                        type="text"
                        value={searchEditData.collectionName || ''}
                        onChange={(e) => setSearchEditData(prev => ({ ...prev, collectionName: e.target.value }))}
                        className="w-full px-3 py-2 border border-green-300 rounded-lg"
                        placeholder="e.g., Enchanted Forest"
                      />
                    </div>

                    {/* Event: Source + Theme */}
                    <div className="border border-orange-200 rounded-lg p-3 bg-orange-50/30">
                      <label className="block text-sm font-medium text-orange-700 mb-2">🎪 Event</label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-orange-600 mb-1">Event Source</label>
                          <input
                            type="text"
                            value={searchEditData.eventSource || ''}
                            onChange={(e) => setSearchEditData(prev => ({ ...prev, eventSource: e.target.value }))}
                            className="w-full px-3 py-2 border border-orange-300 rounded-lg"
                            placeholder="e.g., Polish Pick Up"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-orange-600 mb-1">Event Theme</label>
                          <input
                            type="text"
                            value={searchEditData.eventTheme || ''}
                            onChange={(e) => setSearchEditData(prev => ({ ...prev, eventTheme: e.target.value }))}
                            className="w-full px-3 py-2 border border-orange-300 rounded-lg"
                            placeholder="e.g., Rainbows"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Box: Name + Theme */}
                    <div className="border border-amber-200 rounded-lg p-3 bg-amber-50/30">
                      <label className="block text-sm font-medium text-amber-700 mb-2">📦 Subscription Box</label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-amber-600 mb-1">Box Name</label>
                          <input
                            type="text"
                            value={searchEditData.boxName || ''}
                            onChange={(e) => setSearchEditData(prev => ({ ...prev, boxName: e.target.value }))}
                            className="w-full px-3 py-2 border border-amber-300 rounded-lg"
                            placeholder="e.g., Polished Gamers Box"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-amber-600 mb-1">Box Theme</label>
                          <input
                            type="text"
                            value={searchEditData.boxTheme || ''}
                            onChange={(e) => setSearchEditData(prev => ({ ...prev, boxTheme: e.target.value }))}
                            className="w-full px-3 py-2 border border-amber-300 rounded-lg"
                            placeholder="e.g., Retro Gaming"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Exclusive: To + Theme */}
                    <div className="border border-yellow-200 rounded-lg p-3 bg-yellow-50/30">
                      <label className="block text-sm font-medium text-yellow-700 mb-2">⭐ Exclusive</label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-yellow-600 mb-1">Exclusive To</label>
                          <input
                            type="text"
                            value={searchEditData.exclusiveTo || ''}
                            onChange={(e) => setSearchEditData(prev => ({ ...prev, exclusiveTo: e.target.value }))}
                            className="w-full px-3 py-2 border border-yellow-300 rounded-lg"
                            placeholder="e.g., Color4Nails"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-yellow-600 mb-1">Exclusive Theme</label>
                          <input
                            type="text"
                            value={searchEditData.exclusiveTheme || ''}
                            onChange={(e) => setSearchEditData(prev => ({ ...prev, exclusiveTheme: e.target.value }))}
                            className="w-full px-3 py-2 border border-yellow-300 rounded-lg"
                            placeholder="e.g., Black Friday"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Group Custom: Name + Theme */}
                    <div className="border border-cyan-200 rounded-lg p-3 bg-cyan-50/30">
                      <label className="block text-sm font-medium text-cyan-700 mb-2">👥 Group Custom</label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-cyan-600 mb-1">Group Name</label>
                          <input
                            type="text"
                            value={searchEditData.groupName || ''}
                            onChange={(e) => setSearchEditData(prev => ({ ...prev, groupName: e.target.value }))}
                            className="w-full px-3 py-2 border border-cyan-300 rounded-lg"
                            placeholder="e.g., Holo Hangout"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-cyan-600 mb-1">Group Theme</label>
                          <input
                            type="text"
                            value={searchEditData.groupTheme || ''}
                            onChange={(e) => setSearchEditData(prev => ({ ...prev, groupTheme: e.target.value }))}
                            className="w-full px-3 py-2 border border-cyan-300 rounded-lg"
                            placeholder="e.g., Summer Celebration"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Charity: Name + Cause */}
                    <div className="border border-pink-200 rounded-lg p-3 bg-pink-50/30">
                      <label className="block text-sm font-medium text-pink-700 mb-2">💝 Charity</label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-pink-600 mb-1">Charity Name</label>
                          <input
                            type="text"
                            value={searchEditData.charityName || ''}
                            onChange={(e) => setSearchEditData(prev => ({ ...prev, charityName: e.target.value }))}
                            className="w-full px-3 py-2 border border-pink-300 rounded-lg"
                            placeholder="e.g., Red Cross"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-pink-600 mb-1">Charity Cause</label>
                          <input
                            type="text"
                            value={searchEditData.charityCause || ''}
                            onChange={(e) => setSearchEditData(prev => ({ ...prev, charityCause: e.target.value }))}
                            className="w-full px-3 py-2 border border-pink-300 rounded-lg"
                            placeholder="e.g., Disaster Relief"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Collab: With + Theme */}
                    <div className="border border-violet-200 rounded-lg p-3 bg-violet-50/30">
                      <label className="block text-sm font-medium text-violet-700 mb-2">🤝 Collab</label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-violet-600 mb-1">Collab With</label>
                          <input
                            type="text"
                            value={searchEditData.collabWith || ''}
                            onChange={(e) => setSearchEditData(prev => ({ ...prev, collabWith: e.target.value }))}
                            className="w-full px-3 py-2 border border-violet-300 rounded-lg"
                            placeholder="e.g., Simply Nailogical"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-violet-600 mb-1">Collab Theme</label>
                          <input
                            type="text"
                            value={searchEditData.collabTheme || ''}
                            onChange={(e) => setSearchEditData(prev => ({ ...prev, collabTheme: e.target.value }))}
                            className="w-full px-3 py-2 border border-violet-300 rounded-lg"
                            placeholder="e.g., Anniversary Collection"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Convention */}
                    <div className="border border-indigo-200 rounded-lg p-3 bg-indigo-50/30">
                      <label className="block text-sm font-medium text-indigo-700 mb-2">🎫 Convention</label>
                      <input
                        type="text"
                        value={searchEditData.conventionName || ''}
                        onChange={(e) => setSearchEditData(prev => ({ ...prev, conventionName: e.target.value }))}
                        className="w-full px-3 py-2 border border-indigo-300 rounded-lg"
                        placeholder="e.g., Polish Con"
                      />
                    </div>

                    {/* Set/Duo */}
                    <div className="border border-rose-200 rounded-lg p-3 bg-rose-50/30">
                      <label className="block text-sm font-medium text-rose-700 mb-2">💞 Set / Duo / Trio</label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-rose-600 mb-1">Set Name</label>
                          <input
                            type="text"
                            value={searchEditData.setName || ''}
                            onChange={(e) => setSearchEditData(prev => ({ ...prev, setName: e.target.value }))}
                            className="w-full px-3 py-2 border border-rose-300 rounded-lg"
                            placeholder="e.g., Daddy Dean Duo"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-rose-600 mb-1">Set Type</label>
                          <select
                            value={searchEditData.setType || ''}
                            onChange={(e) => setSearchEditData(prev => ({ ...prev, setType: e.target.value }))}
                            className="w-full px-3 py-2 border border-rose-300 rounded-lg"
                          >
                            <option value="">Select...</option>
                            <option value="duo">👯 Duo (2 polishes)</option>
                            <option value="trio">👯‍♀️ Trio (3 polishes)</option>
                            <option value="quad">💅 Quad (4 polishes)</option>
                            <option value="set">🎁 Set (5+ polishes)</option>
                          </select>
                        </div>
                      </div>
                      <p className="text-xs text-rose-500 mt-2 italic">
                        💡 Partner linking coming soon - for now just record the set name and type
                      </p>
                    </div>

                    {/* Release Notes */}
                    <div>
                      <label className="block text-sm font-medium text-glass-secondary font-body mb-1">📝 Release Notes</label>
                      <input
                        type="text"
                        value={searchEditData.releaseNotes || ''}
                        onChange={(e) => setSearchEditData(prev => ({ ...prev, releaseNotes: e.target.value }))}
                        className="w-full px-3 py-2 border border-slate-600 rounded-lg"
                        placeholder="e.g., Re-release, new label"
                      />
                    </div>

                    {/* Tags */}
                    <div className="border border-amber-200 rounded-lg p-3 bg-amber-50/30">
                      <label className="block text-sm font-medium text-amber-700 mb-2">🏷️ Tags</label>
                      <input
                        type="text"
                        value={searchEditData.tags || ''}
                        onChange={(e) => setSearchEditData(prev => ({ ...prev, tags: e.target.value }))}
                        className="w-full px-3 py-2 border border-amber-300 rounded-lg"
                        placeholder="e.g., St. Patrick's Day, barrel, clover (comma-separated)"
                      />
                      <p className="text-xs text-amber-600 mt-1">Separate multiple tags with commas</p>
                    </div>

                    {/* Legacy Fields (if present) */}
                    {(searchEditModal.data.collab || searchEditModal.data.box) && (
                      <div className="border-t pt-4 mt-4">
                        <p className="text-xs text-red-600 font-medium mb-2">⚠️ Legacy Fields (should be migrated)</p>
                        <div className="grid grid-cols-2 gap-4">
                          {searchEditModal.data.collab && (
                            <div>
                              <label className="block text-sm font-medium text-red-700 mb-1">Collab (Legacy)</label>
                              <input
                                type="text"
                                value={searchEditData.collab || ''}
                                onChange={(e) => setSearchEditData(prev => ({ ...prev, collab: e.target.value }))}
                                className="w-full px-3 py-2 border border-red-300 rounded-lg bg-red-500/10"
                              />
                            </div>
                          )}
                          {searchEditModal.data.box && (
                            <div>
                              <label className="block text-sm font-medium text-red-700 mb-1">Box (Legacy)</label>
                              <input
                                type="text"
                                value={searchEditData.box || ''}
                                onChange={(e) => setSearchEditData(prev => ({ ...prev, box: e.target.value }))}
                                className="w-full px-3 py-2 border border-red-300 rounded-lg bg-red-500/10"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => {
                        setSearchEditModal(null);
                        setSearchEditData({});
                      }}
                      className="flex-1 px-4 py-2 border border-slate-600 rounded-lg text-glass-secondary hover:bg-slate-700/50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveSearchEdit}
                      disabled={searchSaving}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {searchSaving ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* STEP: Data Audit */}
        {/* ============================================ */}
        {currentStep === 'audit' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-glass font-heading flex items-center gap-2">
                  <ClipboardList className="w-6 h-6 text-purple-600" />
                  Data Audit Report
                </h2>
                <p className="text-glass-secondary text-sm mt-1">Review all unique values across your database. Click any value to edit it.</p>
              </div>
              <button
                onClick={scanForAuditData}
                disabled={auditLoading}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {auditLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {auditLoading ? 'Scanning...' : 'Scan Database'}
              </button>
            </div>

            {auditLoading && (
              <div className="text-center py-12 text-glass-muted">
                <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin text-purple-600" />
                <p>Scanning all items in database...</p>
              </div>
            )}

            {!auditLoading && !auditData && (
              <div className="text-center py-12 text-glass-muted">
                <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Click "Scan Database" to generate your audit report</p>
              </div>
            )}

            {!auditLoading && auditData && (
              <div className="space-y-4">
                {/* Summary stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-700">{auditData.eventSource.length}</div>
                    <div className="text-xs text-blue-600">Event Sources</div>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-green-700">{auditData.collectionName.length}</div>
                    <div className="text-xs text-green-600">Collections</div>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-orange-700">{auditData.boxName.length}</div>
                    <div className="text-xs text-orange-600">Box Names</div>
                  </div>
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-violet-700">{auditData.collabWith.length}</div>
                    <div className="text-xs text-violet-600">Collaborators</div>
                  </div>
                </div>

                {/* Sort toggle and legacy migration buttons */}
                <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-glass-secondary font-body">Sort by:</span>
                    <button
                      onClick={() => setAuditSortMode('count')}
                      className={`px-3 py-1 text-sm rounded-lg ${auditSortMode === 'count' ? 'bg-purple-600 text-white' : 'bg-white text-glass-secondary hover:bg-slate-700/50'}`}
                    >
                      Count
                    </button>
                    <button
                      onClick={() => setAuditSortMode('alpha')}
                      className={`px-3 py-1 text-sm rounded-lg ${auditSortMode === 'alpha' ? 'bg-purple-600 text-white' : 'bg-white text-glass-secondary hover:bg-slate-700/50'}`}
                    >
                      A-Z
                    </button>
                  </div>
                  
                  {/* Migrate/Fix buttons */}
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    {/* Fix Date-Like Collection Names */}
                    <button
                      onClick={fixDateLikeCollectionNames}
                      disabled={auditApplying}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                      title="Find patterns like 2024/09, 2023/Summer and extract to Year/Season fields"
                    >
                      📅 Fix Date-Only Names
                    </button>
                    
                    {/* Clear Junk/Placeholder Values */}
                    <button
                      onClick={clearJunkCollectionNames}
                      disabled={auditApplying}
                      className="px-3 py-1 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                      title="Clear junk values like ????, N/A, unknown, TBD, etc."
                    >
                      🗑️ Clear Junk Values
                    </button>
                    
                    {auditData.collab.length > 0 && (
                      <button
                        onClick={() => migrateAllLegacyField('collab')}
                        disabled={auditApplying}
                        className="px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        Migrate All Collab ({auditData.collab.reduce((sum, d) => sum + d.count, 0)})
                      </button>
                    )}
                    {auditData.box.length > 0 && (
                      <button
                        onClick={() => migrateAllLegacyField('box')}
                        disabled={auditApplying}
                        className="px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        Migrate All Box ({auditData.box.reduce((sum, d) => sum + d.count, 0)})
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Date Fix Results */}
                {dateFixResults && (
                  <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm">
                    <span className="text-blue-300">
                      📅 Found <strong>{dateFixResults.found}</strong> date-only values → 
                      Fixed <strong>{dateFixResults.fixed}</strong> items (extracted year/season, cleared collectionName)
                    </span>
                    <button 
                      onClick={() => setDateFixResults(null)}
                      className="ml-2 text-blue-600 hover:underline"
                    >
                      Dismiss
                    </button>
                  </div>
                )}
                
                {/* Junk Fix Results */}
                {junkFixResults && (
                  <div className="p-3 bg-red-500/10 border border-red-200 rounded-lg text-sm">
                    <span className="text-red-800">
                      🗑️ Found <strong>{junkFixResults.found}</strong> junk values → 
                      Cleared <strong>{junkFixResults.fixed}</strong> items
                      {junkFixResults.examples.length > 0 && (
                        <span className="block mt-1 text-xs text-red-600">
                          Examples: {junkFixResults.examples.join(', ')}
                        </span>
                      )}
                    </span>
                    <button 
                      onClick={() => setJunkFixResults(null)}
                      className="ml-2 text-red-600 hover:underline"
                    >
                      Dismiss
                    </button>
                  </div>
                )}

                {/* Field sections */}
                {([
                  { key: 'eventSource', label: 'Event Sources', color: 'blue', description: 'PPU, HHC, Indie Pick Up, etc.' },
                  { key: 'eventTheme', label: 'Event Themes', color: 'indigo', description: 'Monthly themes for events' },
                  { key: 'collectionName', label: 'Collection Names', color: 'green', description: 'Named collections like "Enchanted Forest"' },
                  { key: 'boxName', label: 'Subscription Box Names', color: 'orange', description: 'Polished Gamers Box, etc.' },
                  { key: 'boxTheme', label: 'Box Themes', color: 'amber', description: 'Monthly box themes' },
                  { key: 'groupName', label: 'Group Custom Names', color: 'cyan', description: 'Facebook groups, Reddit, etc.' },
                  { key: 'groupTheme', label: 'Group Themes', color: 'teal', description: 'Group custom themes' },
                  { key: 'exclusiveTo', label: 'Exclusive Retailers', color: 'yellow', description: 'Where exclusives were sold' },
                  { key: 'exclusiveTheme', label: 'Exclusive Themes', color: 'lime', description: 'Black Friday, launch, etc.' },
                  { key: 'charityName', label: 'Charity Names', color: 'rose', description: 'Charities supported' },
                  { key: 'charityCause', label: 'Charity Causes', color: 'pink', description: 'The causes supported' },
                  { key: 'conventionName', label: 'Convention Names', color: 'emerald', description: 'Polish Con, etc.' },
                  { key: 'collabWith', label: 'Collab With', color: 'violet', description: 'Who the collab was with' },
                  { key: 'collabTheme', label: 'Collab Themes', color: 'purple', description: 'Collab occasion/theme' },
                  { key: 'setName', label: '💞 Set Names', color: 'rose', description: 'Duo, trio, quad names' },
                  { key: 'setType', label: '💞 Set Types', color: 'red', description: 'duo, trio, quad, set' },
                  { key: 'collab', label: '⚠️ Collab (Legacy)', color: 'gray', description: 'Old collab field - should be migrated' },
                  { key: 'box', label: '⚠️ Box (Legacy)', color: 'gray', description: 'Old box field - should be migrated' },
                  { key: 'releaseType', label: 'Release Types', color: 'slate', description: 'core, collection, event, etc.' },
                  { key: 'releaseSeason', label: 'Release Seasons', color: 'sky', description: 'Spring, Summer, Fall, Winter' },
                  { key: 'brand', label: 'Brands', color: 'neutral', description: 'All polish brands' },
                  { key: 'category', label: 'Item Categories', color: 'fuchsia', description: 'Nail Lacquer, Gel Polish, Plates, etc.' },
                  { key: 'releaseNotes', label: 'Release Notes', color: 'amber', description: 'Notes about releases, re-releases, etc.' },
                  { key: 'originalReleaseSeason', label: 'Original Release Seasons', color: 'orange', description: 'For re-releases: when it first came out' },
                ] as const).map(({ key, label, color, description }) => {
                  const rawFieldData = auditData[key as keyof AuditData];
                  const isExpanded = auditExpandedSections.has(key);
                  const isLegacy = key === 'collab' || key === 'box';
                  
                  if (rawFieldData.length === 0) return null;
                  
                  // Sort based on current sort mode
                  const fieldData = [...rawFieldData].sort((a, b) => {
                    if (auditSortMode === 'alpha') {
                      return a.value.localeCompare(b.value, undefined, { sensitivity: 'base' });
                    }
                    return b.count - a.count; // Default: by count descending
                  });
                  
                  return (
                    <div key={key} className={`border rounded-lg overflow-hidden ${isLegacy ? 'border-red-300 bg-red-500/10' : 'border-slate-600'}`}>
                      <div className={`flex items-center justify-between p-3 ${isLegacy ? 'bg-red-500/20' : 'bg-slate-800/30'}`}>
                        <button
                          onClick={() => {
                            setAuditExpandedSections(prev => {
                              const next = new Set(prev);
                              if (next.has(key)) {
                                next.delete(key);
                              } else {
                                next.add(key);
                              }
                              return next;
                            });
                          }}
                          className="flex items-center gap-3 hover:bg-slate-700/50 rounded px-2 py-1 -ml-2"
                        >
                          {isExpanded ? <ChevronDown className="w-4 h-4 text-glass-muted" /> : <ChevronRight className="w-4 h-4 text-glass-muted" />}
                          <span className="font-medium text-glass font-body">{label}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold bg-${color}-100 text-${color}-700`}>
                            {fieldData.length} unique
                          </span>
                          <span className="text-xs text-glass-muted">{description}</span>
                        </button>
                        <div className="flex items-center gap-3">
                          {/* Migrate All button for legacy fields */}
                          {isLegacy && fieldData.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                migrateAllLegacyField(key as 'box' | 'collab');
                              }}
                              disabled={auditApplying}
                              className="text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                            >
                              <ArrowRight className="w-3 h-3" />
                              Migrate All ({fieldData.reduce((sum, d) => sum + d.count, 0)})
                            </button>
                          )}
                          <span className="text-sm text-glass-muted">
                            {fieldData.reduce((sum, d) => sum + d.count, 0)} total items
                          </span>
                        </div>
                      </div>
                      
                      {isExpanded && (
                        <div className="max-h-64 overflow-y-auto border-t border-slate-600">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-700/50 sticky top-0">
                              <tr>
                                <th className="text-left px-3 py-2 font-medium text-glass-secondary">Value</th>
                                <th className="text-right px-3 py-2 font-medium text-glass-secondary w-20">Count</th>
                                <th className="text-right px-3 py-2 font-medium text-glass-secondary w-32">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {fieldData.map((item, idx) => (
                                <tr key={idx} className={`border-b border-slate-700 hover:bg-slate-700/50 ${isLegacy ? 'bg-red-500/10' : ''}`}>
                                  <td className="px-3 py-2 text-gray-900">{item.value || <span className="text-slate-400 italic">(empty)</span>}</td>
                                  <td className="px-3 py-2 text-right text-glass-secondary">{item.count}</td>
                                  <td className="px-3 py-2 text-right">
                                    <div className="flex items-center gap-2 justify-end">
                                      {/* Migrate button for legacy fields */}
                                      {isLegacy && (
                                        <button
                                          onClick={() => {
                                            if (window.confirm(`Migrate ${item.count} items from "${key}" to "${key === 'box' ? 'boxName' : 'collabWith'}"?`)) {
                                              applyAuditMigrate(key as 'box' | 'collab', item.value, item.itemIds);
                                            }
                                          }}
                                          disabled={auditApplying}
                                          className="text-xs text-green-600 hover:underline flex items-center gap-1 disabled:opacity-50"
                                        >
                                          <ArrowRight className="w-3 h-3" />
                                          Migrate
                                        </button>
                                      )}
                                      {/* Move button for non-legacy fields */}
                                      {!isLegacy && (
                                        <button
                                          onClick={() => {
                                            setAuditMoveModal({ fromField: key as keyof AuditData, value: item.value, itemIds: item.itemIds });
                                            setAuditMoveTargetField('');
                                          }}
                                          className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                        >
                                          <ArrowRight className="w-3 h-3" />
                                          Move
                                        </button>
                                      )}
                                      {/* Edit button for all fields */}
                                      <button
                                        onClick={() => {
                                          setAuditEditModal({ field: key as keyof AuditData, oldValue: item.value, itemIds: item.itemIds });
                                          setAuditEditNewValue(item.value);
                                        }}
                                        className="text-xs text-purple-600 hover:underline flex items-center gap-1"
                                      >
                                        <Pencil className="w-3 h-3" />
                                        Edit
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Edit Modal */}
            {auditEditModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                  <h3 className="text-lg font-bold text-glass font-heading mb-4">
                    Edit {auditEditModal.field}
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-glass-secondary font-body mb-1">Current Value</label>
                      <div className="px-3 py-2 bg-red-500/10 border border-red-200 rounded-lg text-red-800">
                        "{auditEditModal.oldValue}"
                      </div>
                      <p className="text-xs text-glass-muted mt-1">
                        This value is used by {auditEditModal.itemIds.length} items
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-glass-secondary font-body mb-1">New Value</label>
                      <input
                        type="text"
                        value={auditEditNewValue}
                        onChange={(e) => setAuditEditNewValue(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Enter new value (or leave empty to clear)"
                      />
                      <p className="text-xs text-glass-muted mt-1">
                        Leave empty to clear this field from all items
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => {
                        setAuditEditModal(null);
                        setAuditEditNewValue('');
                      }}
                      className="flex-1 px-4 py-2 border border-slate-600 rounded-lg text-glass-secondary hover:bg-slate-700/50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={applyAuditEdit}
                      disabled={auditApplying || auditEditNewValue === auditEditModal.oldValue}
                      className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {auditApplying ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Applying...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Update {auditEditModal.itemIds.length} Items
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Move Modal */}
            {auditMoveModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                  <h3 className="text-lg font-bold text-glass font-heading mb-4">
                    Move to Different Field
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-glass-secondary font-body mb-1">Current Location</label>
                      <div className="px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded-lg text-blue-300">
                        <span className="font-medium">{auditMoveModal.fromField}</span>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-glass-secondary font-body mb-1">Value to Move</label>
                      <div className="px-3 py-2 bg-slate-800/30 border border-slate-600 rounded-lg text-glass">
                        "{auditMoveModal.value}"
                      </div>
                      <p className="text-xs text-glass-muted mt-1">
                        This value is used by {auditMoveModal.itemIds.length} items
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-glass-secondary font-body mb-1">Move to Field</label>
                      <select
                        value={auditMoveTargetField}
                        onChange={(e) => setAuditMoveTargetField(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select target field...</option>
                        <optgroup label="Release Info">
                          <option value="collectionName">📚 Collection Name</option>
                          <option value="eventSource">🎪 Event Source (PPU, HHC, etc.)</option>
                          <option value="eventTheme">🎨 Event Theme</option>
                          <option value="boxName">📦 Box Name</option>
                          <option value="boxTheme">🎁 Box Theme</option>
                          <option value="groupName">👥 Group Name</option>
                          <option value="groupTheme">💬 Group Theme</option>
                          <option value="exclusiveTo">⭐ Exclusive To</option>
                          <option value="exclusiveTheme">✨ Exclusive Theme</option>
                          <option value="charityName">💝 Charity Name</option>
                          <option value="charityCause">🎗️ Charity Cause</option>
                          <option value="conventionName">🎫 Convention Name</option>
                        </optgroup>
                        <optgroup label="Collab Info">
                          <option value="collabWith">🤝 Collab With</option>
                          <option value="collabTheme">🎨 Collab Theme</option>
                        </optgroup>
                        <optgroup label="Set/Duo Info">
                          <option value="setName">💞 Set Name</option>
                        </optgroup>
                        <optgroup label="Other">
                          <option value="releaseNotes">📝 Release Notes</option>
                        </optgroup>
                      </select>
                      {auditMoveTargetField && (
                        <div className="text-xs mt-1 space-y-1">
                          <p className="text-green-600">
                            {auditMoveTargetField === 'boxName' && '✓ Will set releaseType to "box"'}
                            {(auditMoveTargetField === 'eventSource' || auditMoveTargetField === 'eventTheme') && '✓ Will set releaseType to "event"'}
                            {auditMoveTargetField === 'collectionName' && '✓ Will set releaseType to "collection"'}
                            {auditMoveTargetField === 'exclusiveTo' && '✓ Will set releaseType to "exclusive"'}
                            {auditMoveTargetField === 'charityName' && '✓ Will set releaseType to "charity"'}
                            {auditMoveTargetField === 'groupName' && '✓ Will set releaseType to "groupCustom"'}
                            {auditMoveTargetField === 'conventionName' && '✓ Will set releaseType to "convention"'}
                          </p>
                          {/* Theme migration hints */}
                          {auditMoveModal.fromField === 'eventSource' && auditMoveTargetField === 'boxName' && (
                            <p className="text-blue-600">🔄 Will also move eventTheme → boxTheme</p>
                          )}
                          {auditMoveModal.fromField === 'eventSource' && auditMoveTargetField === 'groupName' && (
                            <p className="text-blue-600">🔄 Will also move eventTheme → groupTheme</p>
                          )}
                          {auditMoveModal.fromField === 'eventSource' && auditMoveTargetField === 'exclusiveTo' && (
                            <p className="text-blue-600">🔄 Will also move eventTheme → exclusiveTheme</p>
                          )}
                          {auditMoveModal.fromField === 'eventSource' && auditMoveTargetField === 'charityName' && (
                            <p className="text-blue-600">🔄 Will also move eventTheme → charityCause</p>
                          )}
                          {auditMoveModal.fromField === 'eventSource' && auditMoveTargetField === 'collabWith' && (
                            <p className="text-blue-600">🔄 Will also move eventTheme → collabTheme</p>
                          )}
                          {auditMoveModal.fromField === 'boxName' && auditMoveTargetField === 'eventSource' && (
                            <p className="text-blue-600">🔄 Will also move boxTheme → eventTheme</p>
                          )}
                          {auditMoveModal.fromField === 'boxName' && auditMoveTargetField === 'groupName' && (
                            <p className="text-blue-600">🔄 Will also move boxTheme → groupTheme</p>
                          )}
                          {auditMoveModal.fromField === 'groupName' && auditMoveTargetField === 'eventSource' && (
                            <p className="text-blue-600">🔄 Will also move groupTheme → eventTheme</p>
                          )}
                          {auditMoveModal.fromField === 'groupName' && auditMoveTargetField === 'boxName' && (
                            <p className="text-blue-600">🔄 Will also move groupTheme → boxTheme</p>
                          )}
                          {auditMoveModal.fromField === 'exclusiveTo' && auditMoveTargetField === 'eventSource' && (
                            <p className="text-blue-600">🔄 Will also move exclusiveTheme → eventTheme</p>
                          )}
                          {auditMoveModal.fromField === 'collabWith' && auditMoveTargetField === 'eventSource' && (
                            <p className="text-blue-600">🔄 Will also move collabTheme → eventTheme</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => {
                        setAuditMoveModal(null);
                        setAuditMoveTargetField('');
                      }}
                      className="flex-1 px-4 py-2 border border-slate-600 rounded-lg text-glass-secondary hover:bg-slate-700/50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={applyAuditMove}
                      disabled={auditApplying || !auditMoveTargetField}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {auditApplying ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Moving...
                        </>
                      ) : (
                        <>
                          <ArrowRight className="w-4 h-4" />
                          Move {auditMoveModal.itemIds.length} Items
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* STEP: Summary */}
        {/* ============================================ */}
        {currentStep === 'summary' && (
          <div>
            <h2 className="text-xl font-bold text-glass font-heading flex items-center gap-2 mb-6">
              <CheckCircle className="w-6 h-6 text-green-600" />
              Cleanup Summary
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="text-3xl font-bold text-purple-600">{appliedCounts.barcode}</div>
                <div className="text-sm text-purple-700">Barcodes extracted from names</div>
              </div>
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="text-3xl font-bold text-blue-600">{appliedCounts.whitespace}</div>
                <div className="text-sm text-blue-700">Whitespace issues fixed</div>
              </div>
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="text-3xl font-bold text-green-600">{appliedCounts.release}</div>
                <div className="text-sm text-green-700">Release types corrected</div>
              </div>
              <div className="p-4 bg-pink-50 border border-pink-200 rounded-lg">
                <div className="text-3xl font-bold text-pink-600">{appliedCounts.parseCollection}</div>
                <div className="text-sm text-pink-700">Collection fields parsed</div>
              </div>
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="text-3xl font-bold text-amber-600">{destashItems.length}</div>
                <div className="text-sm text-amber-700">Destash issues found (need user review)</div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <h3 className="font-bold text-blue-900 mb-2">💡 Next Steps</h3>
              <ul className="text-blue-300 text-sm space-y-1">
                <li>• Run "Populate Brands" to update brand stats</li>
                <li>• Check "Similar Brand Names" for remaining duplicates</li>
                <li>• Check "Similar Item Names" for naming inconsistencies</li>
                <li>• Reach out to users with destash issues</li>
                <li>• Review items marked as "TAG" patterns for manual cleanup</li>
                <li>• Run this wizard periodically after new imports</li>
              </ul>
            </div>

            <div className="mt-6">
              <Link to="/admin" className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                <ArrowLeft className="w-4 h-4" />
                Back to Admin Dashboard
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <button onClick={goToPrevStep} disabled={currentStepIndex === 0} className="px-4 py-2 border border-slate-600 text-glass-secondary rounded-lg hover:bg-slate-700/50 disabled:opacity-50 flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Previous
        </button>

        <button onClick={goToNextStep} disabled={currentStepIndex === steps.length - 1} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2">
          Next
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
